import React, { useEffect, useState, useCallback } from "react";
import {
  searchUsers,
  adminChangesUserData,
  pullUserData,
  verifyEmailManually,
  getPaidReceiptsBySeason,
  getAllUsers,
  getUserStatistics,
  getControlStats,
  getAllSeasons,
  cloneSeasonById,
  getReceiptsBySeasonId,
  getUserNamesByIds,
  getUsersByIds,
  findUserByEmail,
  createCouponByEmail,
  getAllCoupons,
  deleteCoupon,
  sendBadgeMails,
  generateBadges,
  listBadges,
  setProductRequiresTc,
  setProductRequiresWifi,
  setProductClosed,
  adminChangeWorkshop,
  adminTransferRegistration,
} from "../../db/firebaseMethods";
import AppContext from "../../AppContext";
import LoadingBlock from "../../components/organisms/loadingBlock/loadingBlock";
import { utils, writeFileXLSX } from "xlsx";
import moment from "moment/moment";
import "moment/locale/tr";

// MUI
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Collapse,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  InputLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Slide,
  Snackbar,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";

// MUI Icons
import PeopleIcon from "@mui/icons-material/People";
import BarChartIcon from "@mui/icons-material/BarChart";
import EventIcon from "@mui/icons-material/Event";
import SearchIcon from "@mui/icons-material/Search";
import DownloadIcon from "@mui/icons-material/Download";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import HistoryIcon from "@mui/icons-material/History";
import FactCheckIcon from "@mui/icons-material/FactCheck";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import LocalOfferIcon from "@mui/icons-material/LocalOffer";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import SchoolIcon from "@mui/icons-material/School";
import WifiIcon from "@mui/icons-material/Wifi";
import ConfirmationNumberIcon from "@mui/icons-material/ConfirmationNumber";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import { downloadWorkshopPdf } from "./workshopPdf";
import { YAKA_KATEGORILER, readNamesFromExcel, downloadYakaKartiPdf } from "./yakaKartiPdf";
import BadgeIcon from "@mui/icons-material/Badge";

// ── Tema sabitleri ───────────────────────────────────────────────────
const C = {
  pink:      "#ec398b",
  pinkDark:  "#c4106a",
  pinkLight: "#fde8f3",
  pinkMid:   "#f472b6",
  sidebar:   "#1a1a2e",
  sidebarHover: "#16213e",
  sidebarActive: "#ec398b",
  bg:        "#f8f9fb",
  surface:   "#ffffff",
  border:    "#e8eaed",
  text:      "#1a1a2e",
  muted:     "#6b7280",
  green:     "#16a34a",
  greenBg:   "#dcfce7",
  orange:    "#ea580c",
  orangeBg:  "#fff7ed",
  red:       "#dc2626",
  redBg:     "#fee2e2",
  blue:      "#1d4ed8",
  blueBg:    "#dbeafe",
};

// ── Nav tanımları ────────────────────────────────────────────────────
const NAV = [
  { id: "users",   label: "Kullanıcılar",     icon: PeopleIcon },
  { id: "season",  label: "Sezon Yönetimi",   icon: EventIcon },
  { id: "tickets", label: "Bilet (QR) Mailleri", icon: ConfirmationNumberIcon },
  { id: "cards",   label: "Yaka Kartları",    icon: BadgeIcon },
  { id: "coupons", label: "Kuponlar",         icon: LocalOfferIcon },
  { id: "stats",   label: "İstatistikler",    icon: BarChartIcon },
  { id: "control", label: "Kontrol",          icon: FactCheckIcon },
  { id: "nonbuyers", label: "Mailing İçin E-postalar", icon: MailOutlineIcon },
  { id: "workshops", label: "Atölyeler",        icon: SchoolIcon },
  { id: "wifi",    label: "Wifi",             icon: WifiIcon },
  { id: "history", label: "Geçmiş Sezonlar",  icon: HistoryIcon },
];

// ════════════════════════════════════════════════════════════════════
// ANA COMPONENT
// ════════════════════════════════════════════════════════════════════
export default function AdminPage() {
  const { user, activeSeason } = React.useContext(AppContext);
  const [activeTab, setActiveTab] = useState("users");
  const [sidebarOpen, setSidebarOpen] = useState(true);

  // App.css ".App { max-width: 1280px }" sınırını admin sayfasında biraz genişlet
  useEffect(() => {
    const el = document.querySelector(".App");
    if (!el) return undefined;
    const prev = el.style.maxWidth;
    el.style.maxWidth = "1600px";
    return () => { el.style.maxWidth = prev; };
  }, []);

  // Snackbar
  const [snack, setSnack] = useState({ open: false, message: "", severity: "info" });
  const showSnack = (message, severity = "info") => setSnack({ open: true, message, severity });

  return (
    <Box
      sx={{
        display: "flex",
        minHeight: "100vh",
        bgcolor: C.bg,
        fontFamily: "'Inter', sans-serif",
        // App.css'teki ".App * { position: relative }" kuralı MUI'nin Checkbox/Switch
        // içindeki görünmez input ve ripple katmanlarını bozuyor (tıklanamıyor). Geri al:
        "& .PrivateSwitchBase-input, & .MuiSwitch-input, & .MuiSwitch-switchBase, & .MuiTouchRipple-root, & .MuiTouchRipple-root *":
          { position: "absolute" },
      }}
    >

      {/* ── Sidebar ── */}
      <Box
        sx={{
          width: sidebarOpen ? 220 : 0,
          minWidth: sidebarOpen ? 220 : 0,
          overflow: "hidden",
          transition: "all 0.25s ease",
          bgcolor: C.sidebar,
          display: "flex",
          flexDirection: "column",
          flexShrink: 0,
          position: "sticky",
          top: 0,
          height: "100vh",
        }}
      >
        {/* Logo alanı */}
        <Box sx={{ px: 3, pt: 3, pb: 2.5 }}>
          <Typography sx={{ color: C.pink, fontWeight: 800, fontSize: 18, letterSpacing: -0.5 }}>
            Admin Panel
          </Typography>
          {activeSeason && (
            <Chip
              label={activeSeason.id}
              size="small"
              sx={{ mt: 1, bgcolor: "rgba(236,57,139,0.15)", color: C.pinkMid, fontSize: 11, fontWeight: 600 }}
            />
          )}
        </Box>

        <Divider sx={{ borderColor: "rgba(255,255,255,0.06)" }} />

        {/* Nav items */}
        <Box sx={{ flex: 1, pt: 1.5, px: 1.5 }}>
          {NAV.map(({ id, label, icon: Icon }) => {
            const active = activeTab === id;
            return (
              <Box
                key={id}
                onClick={() => setActiveTab(id)}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                  px: 1.5,
                  py: 1.2,
                  borderRadius: 2,
                  mb: 0.5,
                  cursor: "pointer",
                  bgcolor: active ? "rgba(236,57,139,0.18)" : "transparent",
                  borderLeft: active ? `3px solid ${C.pink}` : "3px solid transparent",
                  transition: "all 0.15s",
                  "&:hover": { bgcolor: active ? "rgba(236,57,139,0.18)" : "rgba(255,255,255,0.05)" },
                }}
              >
                <Icon sx={{ fontSize: 18, color: active ? C.pink : "rgba(255,255,255,0.45)" }} />
                <Typography
                  sx={{
                    fontSize: 13.5,
                    fontWeight: active ? 700 : 400,
                    color: active ? "#fff" : "rgba(255,255,255,0.55)",
                    whiteSpace: "nowrap",
                  }}
                >
                  {label}
                </Typography>
              </Box>
            );
          })}
        </Box>

        {/* Alt bilgi */}
        <Box sx={{ px: 2.5, py: 2 }}>
          <Typography sx={{ fontSize: 11, color: "rgba(255,255,255,0.2)" }}>
            {user?.email}
          </Typography>
        </Box>
      </Box>

      {/* ── İçerik alanı ── */}
      <Box sx={{ flex: 1, display: "flex", flexDirection: "column", minWidth: 0 }}>

        {/* Topbar */}
        <Box
          sx={{
            height: 56,
            bgcolor: C.surface,
            borderBottom: `1px solid ${C.border}`,
            display: "flex",
            alignItems: "center",
            px: 3,
            gap: 2,
            position: "sticky",
            top: 0,
            zIndex: 10,
          }}
        >
          <Box
            onClick={() => setSidebarOpen((v) => !v)}
            sx={{ cursor: "pointer", color: C.muted, display: "flex", alignItems: "center" }}
          >
            {sidebarOpen ? <CloseIcon fontSize="small" /> : <MenuIcon fontSize="small" />}
          </Box>
          <Typography sx={{ fontSize: 15, fontWeight: 700, color: C.text }}>
            {NAV.find((n) => n.id === activeTab)?.label}
          </Typography>
        </Box>

        {/* Tab içeriği */}
        <Box sx={{ flex: 1, p: { xs: 2, md: 3 }, overflow: "auto" }}>
          {activeTab === "users"   && <UsersTab user={user} activeSeason={activeSeason} showSnack={showSnack} />}
          {activeTab === "season"  && <SeasonTab activeSeason={activeSeason} showSnack={showSnack} />}
          {activeTab === "tickets" && <TicketMailTab activeSeason={activeSeason} showSnack={showSnack} />}
          {activeTab === "cards"   && <BadgeCardsTab activeSeason={activeSeason} showSnack={showSnack} />}
          {activeTab === "coupons" &&<CouponsTab activeSeason={activeSeason} showSnack={showSnack} />}
          {activeTab === "stats"   && <StatsTab activeSeason={activeSeason} />}
          {activeTab === "control" && <ControlTab activeSeason={activeSeason} />}
          {activeTab === "nonbuyers" && <NonBuyersTab activeSeason={activeSeason} showSnack={showSnack} />}
          {activeTab === "workshops" && <WorkshopsTab activeSeason={activeSeason} showSnack={showSnack} />}
          {activeTab === "wifi" && <WifiTab activeSeason={activeSeason} showSnack={showSnack} />}
          {activeTab === "history" && <PastSeasonsTab showSnack={showSnack} />}
        </Box>
      </Box>

      {/* Global Snackbar */}
      <Snackbar
        open={snack.open}
        autoHideDuration={4000}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert severity={snack.severity} variant="filled" onClose={() => setSnack((s) => ({ ...s, open: false }))}>
          {snack.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

// ════════════════════════════════════════════════════════════════════
// KULLANICILAR TAB
// ════════════════════════════════════════════════════════════════════
function UsersTab({ user, activeSeason, showSnack }) {
  const [search, setSearch]           = useState("");
  const [users, setUsers]             = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [downloading, setDownloading] = useState(false);

  const searchOnAlgolia = async () => {
    if (!user?.algolia_search_api_key) {
      showSnack("Algolia API key bulunamadı!", "error"); return;
    }
    const { searchUsers } = await import("../../db/firebaseMethods");
    const res = await searchUsers(user.algolia_search_api_key, search);
    setUsers(res);
    setSelectedUser(null);
  };

  const onRowSelected = async (u) => {
    const data = await pullUserData(u.objectID);
    setSelectedUser(data);
  };

  const downloadExcel = async () => {
    setDownloading(true);
    try {
      const allUsers  = await getAllUsers();
      const receipts  = await getPaidReceiptsBySeason(activeSeason.id);

      const receiptMap = {};
      const totalPaid  = {};
      const lastPaid   = {};

      receipts.forEach((r) => {
        const row = {};
        Object.keys(activeSeason.products).sort().forEach((pid) => {
          row[pid] = r.receipt_products[pid] === true ? "X" : "";
        });
        if (!receiptMap[r.user_id]) receiptMap[r.user_id] = row;
        else Object.keys(row).forEach((pid) => { receiptMap[r.user_id][pid] = receiptMap[r.user_id][pid] || row[pid]; });
        totalPaid[r.user_id] = (totalPaid[r.user_id] || 0) + (r.price ?? 0);
        if (!lastPaid[r.user_id] || (r.created_at?.seconds > (lastPaid[r.user_id]?.seconds ?? 0)))
          lastPaid[r.user_id] = r.created_at;
      });

      const rows = allUsers.map((u) => {
        const base = {
          ID: u.id, "ADI-SOYADI": u.name, "DOĞUM YILI": u.birth_year, ŞEHİR: u.city,
          EĞİTİM: u.education, EPOSTA: u.email, CİNSİYET: u.gender, MESLEK: u.occupation,
          TELEFON: u.phone, "ÖNCEDEN KATILDI MI": u.previous_attendance,
          "KAYIT SEZONU": u.registration_season, ROL: u.role,
          "NEREDEN DUYDU": u.where_did_you_hear,
          "KAYIT TARİHİ": u.created_at?.seconds ? new Date(u.created_at.seconds * 1000).toLocaleDateString("tr-TR", { timeZone: "Europe/Istanbul" }) : "",
        };
        if (receiptMap[u.id]) Object.assign(base, receiptMap[u.id]);
        base["TOPLAM ÖDEME"] = totalPaid[u.id] || 0;
        base["SON ÖDEME TARİHİ"] = lastPaid[u.id]?.seconds ? new Date(lastPaid[u.id].seconds * 1000).toLocaleDateString("tr-TR", { timeZone: "Europe/Istanbul" }) : "";
        return base;
      });

      const ws = utils.json_to_sheet(rows);
      ws["!cols"] = Array(30).fill({ wch: 22 });
      ws["!cols"][0] = { wch: 35 };
      ws["!cols"][5] = { wch: 35 };
      const wb = utils.book_new();
      utils.book_append_sheet(wb, ws, "Data");
      writeFileXLSX(wb, "Kullanıcı_Listesi.xlsx");
      showSnack("Excel indirildi.", "success");
    } catch (e) {
      showSnack("İndirme hatası: " + e.message, "error");
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Box>
      {/* Araç çubuğu */}
      <Box sx={{ display: "flex", gap: 1.5, mb: 3, flexWrap: "wrap", alignItems: "center" }}>
        <TextField
          size="small"
          placeholder="Ad · Soyad · E-posta · Telefon"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && searchOnAlgolia()}
          sx={{ width: 320, bgcolor: C.surface }}
          InputProps={{ startAdornment: <SearchIcon sx={{ mr: 1, color: C.muted, fontSize: 18 }} /> }}
        />
        <Button
          variant="contained"
          onClick={searchOnAlgolia}
          disabled={search.length < 3}
          sx={btnPink}
        >
          Ara
        </Button>
        <Box sx={{ ml: "auto" }}>
          <Button
            variant="outlined"
            startIcon={downloading ? <CircularProgress size={14} /> : <DownloadIcon />}
            onClick={downloadExcel}
            disabled={downloading || !activeSeason}
            sx={btnOutline}
          >
            Kullanıcıları İndir
          </Button>
        </Box>
      </Box>

      <Box sx={{ display: "flex", gap: 3, alignItems: "flex-start" }}>
        {/* Liste */}
        {users.length > 0 && (
          <TableContainer component={Paper} elevation={0} sx={tablePaper} style={{ flex: 1 }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: "#fafafa" }}>
                  <TableCell sx={th}>E-posta</TableCell>
                  <TableCell sx={th}>Ad-Soyad</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {users.map((u) => (
                  <TableRow
                    hover key={u.email}
                    onClick={() => onRowSelected(u)}
                    sx={{ cursor: "pointer", bgcolor: selectedUser?.email === u.email ? C.pinkLight : "inherit" }}
                  >
                    <TableCell sx={{ fontSize: 13 }}>{u.email}</TableCell>
                    <TableCell sx={{ fontSize: 13 }}>{u.name}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {/* Seçili kullanıcı */}
        {selectedUser && (
          <UserDetailPanel
            selectedUser={selectedUser}
            activeSeason={activeSeason}
            showSnack={showSnack}
            onChanged={() => onRowSelected({ objectID: selectedUser.uid })}
          />
        )}
      </Box>

      {users.length === 0 && (
        <EmptyState icon={<PeopleIcon sx={{ fontSize: 40, color: C.muted }} />} text="Yukarıdan arama yaparak kullanıcıları bulun." />
      )}
    </Box>
  );
}

function UserDetailPanel({ selectedUser, activeSeason, showSnack, onChanged }) {
  const [newPass, setNewPass] = useState("");
  const [loading, setLoading] = useState(false);
  const [changeFrom, setChangeFrom] = useState(null); // değiştirilecek atölye id'si
  const [transferOpen, setTransferOpen] = useState(false);

  const changePass = async () => {
    setLoading(true);
    const res = await adminChangesUserData({ userId: selectedUser.uid, competitorNewPass: newPass });
    showSnack(res, "success");
    setNewPass("");
    setLoading(false);
  };

  const verifyEmail = async () => {
    setLoading(true);
    const res = await verifyEmailManually(selectedUser.uid);
    showSnack(res, "success");
    setLoading(false);
  };

  let paidProducts = [];
  if (selectedUser.activeSeasonReceipts) {
    Object.values(selectedUser.activeSeasonReceipts).forEach((t) => {
      if (t.is_paid) {
        paidProducts.push(
          ...Object.keys(t.receipt_products ?? {}).filter((pid) => t.receipt_products[pid] === true)
        );
      }
    });
  }

  return (
    <Box sx={{ width: 360, bgcolor: C.surface, border: `1px solid ${C.border}`, borderRadius: 2.5, p: 2.5, flexShrink: 0 }}>
      {loading && <LoadingBlock />}

      <Typography sx={{ fontWeight: 700, fontSize: 13, color: C.muted, textTransform: "uppercase", letterSpacing: 0.8, mb: 1.5 }}>
        Seçili Kullanıcı
      </Typography>

      {[
        ["Ad-Soyad", selectedUser.name],
        ["E-posta",  selectedUser.email],
        ["Telefon",  selectedUser.phone],
        ["Doğum Yılı", selectedUser.birth_year],
        ["Cinsiyet", selectedUser.gender],
        ["Eğitim",   selectedUser.education],
        ["Meslek",   selectedUser.occupation],
        ["Şehir",    selectedUser.city],
      ].map(([label, value]) => (
        <Box key={label} sx={{ display: "flex", gap: 1, mb: 0.8 }}>
          <Typography sx={{ fontSize: 12, color: C.muted, minWidth: 80 }}>{label}</Typography>
          <Typography sx={{ fontSize: 12, fontWeight: 600, color: C.text }}>{value || "—"}</Typography>
        </Box>
      ))}

      <Divider sx={{ my: 1.5 }} />

      <Typography sx={{ fontSize: 12, fontWeight: 700, color: C.pink, mb: 1 }}>Ödemesi Alınan Atölyeler</Typography>
      {paidProducts.length === 0 ? (
        <Typography sx={{ fontSize: 12, color: C.muted }}>Henüz ödeme yok.</Typography>
      ) : (
        paidProducts.map((pid) => {
          const isKongre = activeSeason?.products?.[pid]?.product_type === 1;
          return (
            <Box key={pid} sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 0.5 }}>
              <CheckCircleOutlineIcon sx={{ fontSize: 14, color: C.green }} />
              <Typography sx={{ fontSize: 12, flex: 1 }}>{activeSeason?.products?.[pid]?.title ?? pid}</Typography>
              {!isKongre && (
                <Tooltip title="Bu atölyeyi başka bir atölyeyle değiştir">
                  <Button
                    size="small"
                    onClick={() => setChangeFrom(pid)}
                    startIcon={<SwapHorizIcon sx={{ fontSize: 14 }} />}
                    sx={{ fontSize: 11, textTransform: "none", color: C.pinkDark, minWidth: 0, py: 0 }}
                  >
                    Değiştir
                  </Button>
                </Tooltip>
              )}
            </Box>
          );
        })
      )}

      {paidProducts.length > 0 && (
        <Button
          size="small"
          variant="outlined"
          startIcon={<SwapHorizIcon />}
          sx={{ ...btnOutline, mt: 1, width: "100%" }}
          onClick={() => setTransferOpen(true)}
        >
          Kaydı Başka Kişiye Aktar
        </Button>
      )}

      <Divider sx={{ my: 1.5 }} />

      <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
        <TextField
          size="small"
          label="Yeni Şifre"
          value={newPass}
          onChange={(e) => setNewPass(e.target.value)}
          type="password"
          sx={{ flex: 1, minWidth: 130 }}
        />
        <Button
          size="small"
          variant="contained"
          disabled={newPass.length < 6}
          onClick={changePass}
          sx={btnPink}
        >
          Değiştir
        </Button>
      </Box>

      <Button size="small" variant="outlined" sx={{ ...btnOutline, mt: 1, width: "100%" }} onClick={verifyEmail}>
        E-postayı Doğrulanmış Say
      </Button>

      <ChangeWorkshopDialog
        open={!!changeFrom}
        fromProductId={changeFrom}
        user={selectedUser}
        activeSeason={activeSeason}
        showSnack={showSnack}
        onClose={() => setChangeFrom(null)}
        onDone={() => { setChangeFrom(null); onChanged?.(); }}
      />
      <TransferRegistrationDialog
        open={transferOpen}
        user={selectedUser}
        paidProducts={paidProducts}
        activeSeason={activeSeason}
        showSnack={showSnack}
        onClose={() => setTransferOpen(false)}
        onDone={() => { setTransferOpen(false); onChanged?.(); }}
      />
    </Box>
  );
}

// ── Atölye Değiştir Dialog ───────────────────────────────────────────
function ChangeWorkshopDialog({ open, fromProductId, user, activeSeason, showSnack, onClose, onDone }) {
  const [toId, setToId]       = useState("");
  const [busy, setBusy]       = useState(false);
  const [error, setError]     = useState("");
  const [needForce, setNeedForce] = useState(false);

  useEffect(() => { if (open) { setToId(""); setError(""); setNeedForce(false); } }, [open]);

  const products = activeSeason?.products ?? {};
  const owned = new Set();
  (user?.activeSeasonReceipts ?? []).forEach((r) => {
    if (r.is_paid) Object.keys(r.receipt_products ?? {}).forEach((pid) => r.receipt_products[pid] === true && owned.add(pid));
  });
  const options = Object.keys(products)
    .filter((pid) => products[pid]?.product_type !== 1 && !pid.includes("kongre_katilim") && pid !== fromProductId && !owned.has(pid))
    .sort((a, b) => (products[a]?.title ?? a).localeCompare(products[b]?.title ?? b, "tr"));

  const submit = async (force = false) => {
    setBusy(true); setError("");
    try {
      const res = await adminChangeWorkshop({
        seasonId: activeSeason.id, userId: user.uid,
        fromProductId, toProductId: toId, force,
      });
      showSnack(`Atölye değiştirildi: ${res.from} → ${res.to}${res.oversold ? " (stok 0'dı, kontenjan aşıldı)" : ""}`, "success");
      onDone();
    } catch (e) {
      setError(e.message || "İşlem başarısız.");
      if (e.code === "functions/resource-exhausted") setNeedForce(true);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontWeight: 800 }}>Atölye Değiştir</DialogTitle>
      <DialogContent>
        <Typography sx={{ fontSize: 13, mb: 0.5 }}><b>{user?.name}</b></Typography>
        <Typography sx={{ fontSize: 12, color: C.muted, mb: 2 }}>
          Mevcut: <b>{products[fromProductId]?.title ?? fromProductId}</b>
        </Typography>
        <FormControl fullWidth size="small">
          <InputLabel>Yeni atölye</InputLabel>
          <Select label="Yeni atölye" value={toId} onChange={(e) => { setToId(e.target.value); setError(""); setNeedForce(false); }}>
            {options.map((pid) => {
              const stock = products[pid]?.stock ?? 0;
              return (
                <MenuItem key={pid} value={pid}>
                  {products[pid]?.title ?? pid}&nbsp;
                  <span style={{ color: stock > 0 ? C.green : C.red, fontSize: 12 }}>
                    (kalan: {stock})
                  </span>
                </MenuItem>
              );
            })}
          </Select>
        </FormControl>
        <Typography sx={{ fontSize: 11, color: C.muted, mt: 1.5 }}>
          Ödeme ve fiyat aynen kalır. Eski atölyenin stoğu 1 artar, yenisininki 1 azalır.
          Kişi Atölye/Bilet listelerinde otomatik güncellenir.
        </Typography>
        {error && <Alert severity="error" sx={{ mt: 1.5 }}>{error}</Alert>}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={busy} sx={{ textTransform: "none", color: C.muted }}>Vazgeç</Button>
        {needForce && (
          <Button onClick={() => submit(true)} disabled={busy} sx={{ textTransform: "none", color: C.red }}>
            Yine de Ekle
          </Button>
        )}
        <Button variant="contained" onClick={() => submit(false)} disabled={!toId || busy} sx={btnPink}>
          {busy ? <CircularProgress size={16} sx={{ color: "#fff" }} /> : "Değiştir"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ── Kaydı Aktar Dialog ───────────────────────────────────────────────
function TransferRegistrationDialog({ open, user, paidProducts, activeSeason, showSnack, onClose, onDone }) {
  const [email, setEmail]   = useState("");
  const [target, setTarget] = useState(null); // bulunan kullanıcı
  const [looking, setLooking] = useState(false);
  const [busy, setBusy]     = useState(false);
  const [error, setError]   = useState("");

  useEffect(() => { if (open) { setEmail(""); setTarget(null); setError(""); } }, [open]);

  const lookup = async () => {
    setLooking(true); setError(""); setTarget(null);
    try {
      const found = (await findUserByEmail(email.trim())) || (await findUserByEmail(email.trim().toLowerCase()));
      if (!found) setError("Bu e-posta ile kayıtlı kullanıcı bulunamadı. Alıcı önce siteye üye olmalı.");
      else if (found.id === user.uid) setError("Kaynak ve hedef aynı kişi.");
      else setTarget(found);
    } catch (e) {
      setError(e.message || "Arama başarısız.");
    } finally {
      setLooking(false);
    }
  };

  const submit = async () => {
    setBusy(true); setError("");
    try {
      const res = await adminTransferRegistration({
        seasonId: activeSeason.id, fromUserId: user.uid, toUserId: target.id,
      });
      showSnack(
        `${res.receipts} kayıt ${res.from} → ${res.to} kişisine aktarıldı.${res.badgeMoved ? " Bilet de taşındı; yeni QR için bilet mailini tekrar gönderin." : ""}`,
        "success"
      );
      onDone();
    } catch (e) {
      setError(e.message || "İşlem başarısız.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontWeight: 800 }}>Kaydı Başka Kişiye Aktar</DialogTitle>
      <DialogContent>
        <Typography sx={{ fontSize: 12, color: C.muted, mb: 0.5 }}>Aktarılacak (şu anki sahibi)</Typography>
        <Typography sx={{ fontSize: 13, fontWeight: 700 }}>{user?.name}</Typography>
        <Typography sx={{ fontSize: 12, color: C.muted, mb: 1 }}>{user?.email}</Typography>
        <Box sx={{ mb: 2 }}>
          {paidProducts.map((pid) => (
            <Chip key={pid} size="small" label={activeSeason?.products?.[pid]?.title ?? pid}
              sx={{ mr: 0.5, mb: 0.5, bgcolor: C.pinkLight, color: C.pinkDark, fontSize: 11 }} />
          ))}
        </Box>

        <Box sx={{ display: "flex", gap: 1 }}>
          <TextField
            size="small" fullWidth label="Alıcının e-postası" value={email}
            onChange={(e) => { setEmail(e.target.value); setTarget(null); setError(""); }}
            onKeyDown={(e) => e.key === "Enter" && email.includes("@") && lookup()}
          />
          <Button variant="outlined" onClick={lookup} disabled={!email.includes("@") || looking} sx={btnOutline}>
            {looking ? <CircularProgress size={16} /> : "Bul"}
          </Button>
        </Box>

        {target && (
          <Alert severity="info" icon={<ArrowForwardIcon fontSize="small" />} sx={{ mt: 1.5 }}>
            <b>{target.name}</b> ({target.email}) kişisine aktarılacak.
          </Alert>
        )}
        <Typography sx={{ fontSize: 11, color: C.muted, mt: 1.5 }}>
          Bu sezondaki tüm ödenmiş kayıtlar (kongre + atölyeler) alıcıya geçer. Bilet kodu korunur ama
          QR yenilenir; eski kişinin QR'ı geçersiz olur. Alıcıda aynı ürün varsa işlem reddedilir.
          Alıcının TC/wifi bilgisini kendi profilinden girmesi gerekebilir.
        </Typography>
        {error && <Alert severity="error" sx={{ mt: 1.5 }}>{error}</Alert>}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={busy} sx={{ textTransform: "none", color: C.muted }}>Vazgeç</Button>
        <Button variant="contained" onClick={submit} disabled={!target || busy} sx={btnPink}>
          {busy ? <CircularProgress size={16} sx={{ color: "#fff" }} /> : "Aktar"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ════════════════════════════════════════════════════════════════════
// SEZON YÖNETİMİ TAB
// ════════════════════════════════════════════════════════════════════
function SeasonTab({ activeSeason, showSnack }) {
  const [receipts, setReceipts] = useState([]);
  const [userMap, setUserMap]   = useState({});
  const [loading, setLoading]   = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [selectedProductId, setSelectedProductId] = useState(null);
  const [closedMap, setClosedMap] = useState({}); // { [pid]: bool } — anlık UI güncellemesi

  const toggleClosed = async (p) => {
    const next = !p.closed;
    setClosedMap((m) => ({ ...m, [p.id]: next }));
    try {
      await setProductClosed(activeSeason.id, p.id, next);
      showSnack(`"${p.title}" ${next ? "kapatıldı" : "yeniden açıldı"}.`, "success");
    } catch (e) {
      setClosedMap((m) => ({ ...m, [p.id]: !next }));
      showSnack("Güncellenemedi: " + (e.message ?? e), "error");
    }
  };

  useEffect(() => {
    if (!activeSeason?.id) return;
    (async () => {
      setLoading(true);
      const data = (await getPaidReceiptsBySeason(activeSeason.id)).filter((r) => r.is_paid);
      const uniqueIds = [...new Set(data.map((r) => r.user_id).filter(Boolean))];
      const users = await getUsersByIds(uniqueIds);
      setUserMap(users);
      setReceipts(data.map((r) => ({ ...r, name: r.name || users[r.user_id]?.name || "" })));
      setLoading(false);
    })();
  }, [activeSeason?.id]);

  if (!activeSeason) return <EmptyState text="Aktif sezon bulunamadı." />;

  // ── Satış hesapla ──
  const productIds = Object.keys(activeSeason.products ?? {}).sort();
  const soldCounts = {};
  const buyersByProduct = {};
  receipts.forEach((r) => {
    Object.keys(r.receipt_products ?? {}).forEach((pid) => {
      if (r.receipt_products[pid] === true) {
        soldCounts[pid] = (soldCounts[pid] || 0) + 1;
        if (!buyersByProduct[pid]) buyersByProduct[pid] = [];
        buyersByProduct[pid].push({
          email:   r.email,
          name:    r.name ?? "",
          paid_at: r.paid_at ?? r.created_at,
          pid,
        });
      }
    });
  });

  const totalRev  = receipts.reduce((a, r) => a + (r.price ?? 0), 0);
  const kongreSold = Object.keys(soldCounts)
    .filter((pid) => activeSeason.products?.[pid]?.product_type === 1)
    .reduce((a, pid) => a + soldCounts[pid], 0);
  const workshopSold = Object.keys(soldCounts)
    .filter((pid) => activeSeason.products?.[pid]?.product_type !== 1)
    .reduce((a, pid) => a + soldCounts[pid], 0);

  // ── Ürün listesi (StockPanel'e geçilecek format) ──
  const products = productIds.map((pid) => {
    const p     = activeSeason.products[pid];
    const sold  = soldCounts[pid] ?? 0;
    const total = p?.total_stock ?? 0;
    return {
      id:        pid,
      title:     p?.title ?? pid,
      moderator: p?.moderator ?? "—",
      room:      p?.room ?? "—",
      start_at:  p?.start_at ?? null,
      end_at:    p?.end_at ?? null,
      total,
      sold,
      closed:    closedMap[pid] ?? p?.is_closed === true,
      buyers:    buyersByProduct[pid] ?? [],
    };
  });

  // ── Satın alanlar (kişi başına tek satır) ──
  const buyersMap = {};
  receipts.forEach((r) => {
    const key = r.user_id || (r.email ?? "").trim().toLowerCase() || r.id;
    const u = userMap[r.user_id] ?? {};
    const b = (buyersMap[key] ??= {
      key,
      name:       u.name || r.name || "",
      occupation: u.occupation ?? "",
      city:       u.city ?? "",
      email:      u.email || r.email || "",
      phone:      u.phone || r.phone || "",
      plate:      u.car_plate ?? "",
      products:   new Set(),
    });
    Object.keys(r.receipt_products ?? {})
      .filter((pid) => r.receipt_products[pid] === true)
      .forEach((pid) => b.products.add(pid));
  });
  const buyers = Object.values(buyersMap)
    .map((b) => ({
      ...b,
      products: [...b.products].map((pid) => ({
        id: pid,
        title: activeSeason.products?.[pid]?.title ?? pid,
        isKongre: activeSeason.products?.[pid]?.product_type === 1,
      })),
    }))
    .sort((a, b) => a.name.localeCompare(b.name, "tr"));

  return (
    <Box>
      {/* Başlık */}
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3, flexWrap: "wrap", gap: 1 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Typography variant="h6" fontWeight={800}>Sezon Yönetimi</Typography>
          <Chip label={activeSeason.id} size="small" sx={{ bgcolor: C.pinkLight, color: C.pinkDark, fontWeight: 700 }} />
          {activeSeason.is_active && (
            <Chip label="Aktif" size="small" sx={{ bgcolor: C.greenBg, color: C.green, fontWeight: 700 }} />
          )}
        </Box>
        <Button variant="contained" startIcon={<ContentCopyIcon />} onClick={() => setCreateOpen(true)} sx={btnPink}>
          Yeni Sezon Oluştur
        </Button>
      </Box>

      {/* Özet kartlar */}
      <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 2, mb: 3 }}>
        {[
          { label: "Toplam Gelir",    value: `₺${totalRev.toLocaleString("tr-TR")}`,              color: C.pinkDark,bg: C.pinkLight},
          { label: "Satılan Kongre",  value: kongreSold,                                          color: C.orange,  bg: C.orangeBg },
          { label: "Satılan Atölye",  value: workshopSold,                                        color: "#7c3aed", bg: "#ede9fe" },
        ].map((card) => (
          <Paper key={card.label} elevation={0} sx={{ p: 2, border: `1px solid ${C.border}`, borderRadius: 2.5, bgcolor: card.bg }}>
            <Typography sx={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: 0.6 }}>
              {card.label}
            </Typography>
            <Typography sx={{ fontSize: 24, fontWeight: 800, color: card.color, mt: 0.5 }}>
              {card.value}
            </Typography>
          </Paper>
        ))}
      </Box>

      {loading ? (
        <Box sx={{ textAlign: "center", py: 6 }}><CircularProgress sx={{ color: C.pink }} /></Box>
      ) : (
        <>
          <StockPanel products={products} selectedId={selectedProductId} onSelect={setSelectedProductId} onToggleClosed={toggleClosed} />
          <BuyersPanel
            buyers={buyers}
            seasonId={activeSeason.id}
            productFilter={products.find((p) => p.id === selectedProductId) ?? null}
            onClearFilter={() => setSelectedProductId(null)}
          />
        </>
      )}

      <CreateSeasonModal open={createOpen} onClose={() => setCreateOpen(false)} showSnack={showSnack} />
    </Box>
  );
}

// ════════════════════════════════════════════════════════════════════
// BİLET (QR) MAİLLERİ
// ════════════════════════════════════════════════════════════════════
function TicketMailTab({ activeSeason, showSnack }) {
  if (!activeSeason) return <EmptyState text="Aktif sezon bulunamadı." />;
  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 3 }}>
        <Typography variant="h6" fontWeight={800}>Bilet (QR) Mailleri</Typography>
        <Chip label={activeSeason.id} size="small" sx={{ bgcolor: C.pinkLight, color: C.pinkDark, fontWeight: 700 }} />
      </Box>
      <BadgeMailPanel seasonId={activeSeason.id} showSnack={showSnack} />
    </Box>
  );
}

function BadgeMailPanel({ seasonId, showSnack }) {
  const [testEmail, setTestEmail] = useState("");
  const [onlyEmail, setOnlyEmail] = useState("");
  const [busy, setBusy]           = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [progress, setProgress]   = useState(null); // { sent, total, remaining }
  const [failed, setFailed]       = useState([]);

  const run = async (fn) => {
    setBusy(true);
    try { await fn(); }
    catch (e) { showSnack(e?.message || "İşlem başarısız.", "error"); }
    finally { setBusy(false); }
  };

  const createCodes = () => run(async () => {
    const r = await generateBadges(seasonId);
    showSnack(`${r.created} yeni kod oluşturuldu, ${r.existing} kişinin kodu zaten vardı (toplam ${r.total}).`, "success");
  });

  const downloadList = () => run(async () => {
    const { rows } = await listBadges(seasonId);
    const fmt = (ms) => (ms ? new Date(ms).toLocaleString("tr-TR", { timeZone: "Europe/Istanbul" }) : "");
    const ws = utils.json_to_sheet(rows.map((r) => ({
      KOD: r.code,
      "ADI-SOYADI": r.name,
      EPOSTA: r.email,
      ATÖLYELER: r.workshops.join(" | "),
      "QR İÇERİĞİ": r.qrPayload,
      "MAİL GÖNDERİLDİ": fmt(r.sentAt),
      "GİRİŞ SAATİ": fmt(r.checkedInAt),
    })));
    ws["!cols"] = [{ wch: 12 }, { wch: 28 }, { wch: 32 }, { wch: 60 }, { wch: 24 }, { wch: 20 }, { wch: 20 }];
    const wb = utils.book_new();
    utils.book_append_sheet(wb, ws, "Biletler");
    writeFileXLSX(wb, `Bilet_Listesi_${seasonId}.xlsx`);
  });

  const sendTest = () => run(async () => {
    await sendBadgeMails({ mode: "test", seasonId, testEmail: testEmail.trim() });
    showSnack(`Test bileti ${testEmail.trim()} adresine gönderildi.`, "success");
  });

  const sendOne = () => run(async () => {
    const r = await sendBadgeMails({ mode: "send", seasonId, onlyEmail: onlyEmail.trim() });
    if (r.failed.length) throw new Error(r.failed[0].error);
    showSnack(`Bilet ${onlyEmail.trim()} adresine gönderildi.`, "success");
  });

  const sendAll = () => run(async () => {
    setConfirmOpen(false);
    setFailed([]);
    let sentTotal = 0;
    const allFailed = [];
    // Sunucu her çağrıda sınırlı sayıda kişiye yollar; gönderilmemiş kimse kalmayana kadar devam.
    for (;;) {
      const r = await sendBadgeMails({
        mode: "send",
        seasonId,
        skipEmails: allFailed.map((f) => f.email),
      });
      sentTotal += r.sent;
      allFailed.push(...r.failed);
      setFailed([...allFailed]);
      setProgress({ sent: sentTotal, total: r.total, remaining: r.remaining });
      if (r.remaining <= 0 || (r.sent === 0 && r.failed.length === 0)) break;
    }
    showSnack(
      `${sentTotal} bilet gönderildi${allFailed.length ? `, ${allFailed.length} hata var` : ""}.`,
      allFailed.length ? "warning" : "success"
    );
  });

  const pct = progress && progress.total ? Math.min(100, Math.round(((progress.total - progress.remaining) / progress.total) * 100)) : 0;

  return (
    <Paper elevation={0} sx={{ p: 2.5, mb: 3, border: `1px solid ${C.border}`, borderRadius: 2.5 }}>
      <Typography sx={{ fontWeight: 800, mb: 0.5 }}>Bilet (QR) Mailleri</Typography>
      <Typography sx={{ fontSize: 13, color: C.muted, mb: 2 }}>
        Kongre bileti alan herkese ad, atölyeler ve QR kodu içeren bilet gönderilir. Daha önce gönderilenlere tekrar gitmez.
      </Typography>

      <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", alignItems: "center", mb: 2 }}>
        <Button variant="outlined" disabled={busy} onClick={createCodes} sx={{ textTransform: "none", fontWeight: 700, borderRadius: 2 }}>
          Bilet kodlarını önceden oluştur
        </Button>
        <Button variant="outlined" disabled={busy} onClick={downloadList} sx={{ textTransform: "none", fontWeight: 700, borderRadius: 2 }}>
          Bilet listesini Excel indir
        </Button>
        <Typography sx={{ fontSize: 12, color: C.muted }}>
          Mail atmadan herkesin kodu (XII-0001…) ödeme sırasına göre belirlenir. Tekrar çalıştırmak güvenlidir.
        </Typography>
      </Box>

      <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", alignItems: "center", mb: 1.5 }}>
        <TextField size="small" label="Test e-postası" value={testEmail} onChange={(e) => setTestEmail(e.target.value)} sx={{ minWidth: 260 }} />
        <Button variant="outlined" disabled={busy || !testEmail.trim()} onClick={sendTest} sx={{ textTransform: "none", fontWeight: 700, borderRadius: 2 }}>
          Test Gönder
        </Button>
      </Box>
      <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", alignItems: "center", mb: 2 }}>
        <TextField size="small" label="Tek kişiye (yeniden) gönder" value={onlyEmail} onChange={(e) => setOnlyEmail(e.target.value)} sx={{ minWidth: 260 }} />
        <Button variant="outlined" disabled={busy || !onlyEmail.trim()} onClick={sendOne} sx={{ textTransform: "none", fontWeight: 700, borderRadius: 2 }}>
          Gönder
        </Button>
      </Box>

      <Button variant="contained" startIcon={<MailOutlineIcon />} disabled={busy} onClick={() => setConfirmOpen(true)} sx={btnPink}>
        Herkese Gönder
      </Button>

      {progress && (
        <Box sx={{ mt: 2 }}>
          <LinearProgress variant="determinate" value={pct} sx={{ height: 8, borderRadius: 4, mb: 0.5 }} />
          <Typography sx={{ fontSize: 12, color: C.muted }}>
            {progress.sent} gönderildi · {progress.remaining} kaldı (toplam {progress.total})
          </Typography>
        </Box>
      )}
      {failed.length > 0 && (
        <Alert severity="warning" sx={{ mt: 2 }}>
          Gönderilemeyenler: {failed.map((f) => `${f.name || "-"} <${f.email || "e-posta yok"}> (${f.error})`).join(" · ")}
        </Alert>
      )}

      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <DialogTitle>Biletler herkese gönderilsin mi?</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: 14 }}>
            Kongre bileti alan ve henüz bilet almamış tüm kişilere e-posta gidecek. Bu işlem geri alınamaz. Önce test mailini kontrol ettiğinizden emin olun.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)} sx={{ textTransform: "none" }}>Vazgeç</Button>
          <Button variant="contained" onClick={sendAll} sx={btnPink}>Evet, Gönder</Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
}

// ════════════════════════════════════════════════════════════════════
// SATIN ALAN BİLGİLERİ
// ════════════════════════════════════════════════════════════════════
function BuyersPanel({ buyers, seasonId, productFilter, onClearFilter }) {
  const [search, setSearch] = useState("");

  const q = search.toLocaleLowerCase("tr");
  const visible = buyers.filter((b) =>
    (!productFilter || b.products.some((p) => p.id === productFilter.id)) &&
    (!q || [b.name, b.email, b.phone, b.occupation, b.city].some((v) => String(v ?? "").toLocaleLowerCase("tr").includes(q)))
  );

  // Atölyeler kişi başına ayrı sütunlarda (Atölye 1, Atölye 2, …)
  const kongreOf    = (b) => b.products.filter((p) => p.isKongre);
  const workshopsOf = (b) => b.products.filter((p) => !p.isKongre);
  const maxWorkshops = Math.max(0, ...visible.map((b) => workshopsOf(b).length));
  const workshopCols = Array.from({ length: maxWorkshops }, (_, i) => i);

  const downloadExcel = () => {
    const rows = visible.map((b) => {
      const ws = workshopsOf(b);
      const row = {
        "ADI-SOYADI": b.name,
        MESLEK:       b.occupation,
        İL:           b.city,
        EPOSTA:       b.email,
        TELEFON:      b.phone,
        KONGRE:       kongreOf(b).map((p) => p.title).join(" | "),
      };
      workshopCols.forEach((i) => { row[`ATÖLYE ${i + 1}`] = ws[i]?.title ?? ""; });
      return row;
    });
    const ws = utils.json_to_sheet(rows);
    ws["!cols"] = [{ wch: 28 }, { wch: 24 }, { wch: 16 }, { wch: 32 }, { wch: 16 }, { wch: 30 }, ...workshopCols.map(() => ({ wch: 40 }))];
    const wb = utils.book_new();
    utils.book_append_sheet(wb, ws, "Satın Alanlar");
    writeFileXLSX(wb, `Satin_Alanlar_${seasonId}${productFilter ? "_" + productFilter.id : ""}.xlsx`);
  };

  // Tüm satın alanların plakaları (arama/atölye filtresinden bağımsız)
  const downloadPlates = () => {
    const rows = buyers.map((b) => ({
      "ADI-SOYADI": b.name,
      PLAKA:        b.plate,
      EPOSTA:       b.email,
      TELEFON:      b.phone,
      KONGRE:       kongreOf(b).length ? "Evet" : "Hayır",
    }));
    const ws = utils.json_to_sheet(rows);
    ws["!cols"] = [{ wch: 28 }, { wch: 16 }, { wch: 32 }, { wch: 16 }, { wch: 10 }];
    const wb = utils.book_new();
    utils.book_append_sheet(wb, ws, "Plakalar");
    writeFileXLSX(wb, `Katilimci_Plakalari_${seasonId}.xlsx`);
  };

  return (
    <Box sx={{ mt: 4 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2, flexWrap: "wrap" }}>
        <Typography variant="h6" fontWeight={800} sx={{ fontSize: 16 }}>Satın Alan Bilgileri</Typography>
        <Chip label={`${visible.length} kişi`} size="small" sx={{ bgcolor: C.pinkLight, color: C.pinkDark, fontWeight: 700 }} />
        {productFilter && (
          <Chip
            label={productFilter.title}
            size="small"
            onDelete={onClearFilter}
            sx={{ maxWidth: 320, bgcolor: C.pink, color: "#fff", fontWeight: 700, "& .MuiChip-deleteIcon": { color: "#fff" } }}
          />
        )}
        <Box sx={{ flex: 1 }} />
        <TextField
          size="small"
          placeholder="İsim, e-posta, telefon, meslek veya il ara…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ width: 300, bgcolor: C.surface }}
          InputProps={{ startAdornment: <SearchIcon sx={{ mr: 0.5, fontSize: 16, color: C.muted }} /> }}
        />
        <Button size="small" startIcon={<DownloadIcon />} onClick={downloadExcel} disabled={!visible.length}>
          Excel
        </Button>
        <Button size="small" startIcon={<DownloadIcon />} onClick={downloadPlates} disabled={!buyers.length}>
          Plakalar (Excel)
        </Button>
      </Box>

      {visible.length === 0 ? (
        <EmptyState text={search ? "Sonuç bulunamadı." : productFilter ? "Bu atölye için henüz satış yok." : "Bu sezonda henüz satın alan yok."} />
      ) : (
        <TableContainer component={Paper} elevation={0} sx={{ ...tablePaper, maxHeight: 520, overflow: "auto" }}>
          <Table size="small" stickyHeader sx={{ "& td, & th": { verticalAlign: "top" } }}>
            <TableHead>
              <TableRow>
                {["Ad-Soyad", "Meslek", "İl", "E-posta", "Telefon", "Kongre", ...workshopCols.map((i) => `Atölye ${i + 1}`)].map((h) => (
                  <TableCell key={h} sx={{ ...th, bgcolor: "#fafafa" }}>{h}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {visible.map((b) => (
                <TableRow hover key={b.key}>
                  <TableCell sx={{ fontSize: 13, fontWeight: 600, whiteSpace: "nowrap" }}>{b.name || "—"}</TableCell>
                  <TableCell sx={{ fontSize: 13, whiteSpace: "nowrap" }}>{b.occupation || "—"}</TableCell>
                  <TableCell sx={{ fontSize: 13, whiteSpace: "nowrap" }}>{b.city || "—"}</TableCell>
                  <TableCell sx={{ fontSize: 13, whiteSpace: "nowrap" }}>{b.email || "—"}</TableCell>
                  <TableCell sx={{ fontSize: 13, whiteSpace: "nowrap" }}>{b.phone || "—"}</TableCell>
                  <TableCell>
                    {kongreOf(b).length ? (
                      <Tooltip title={kongreOf(b).map((p) => p.title).join(" | ")} placement="top">
                        <Chip label="Kongre" size="small" sx={{ fontSize: 11, fontWeight: 600, bgcolor: C.orangeBg, color: C.orange }} />
                      </Tooltip>
                    ) : "—"}
                  </TableCell>
                  {workshopCols.map((i) => {
                    const p = workshopsOf(b)[i];
                    return (
                      <TableCell key={i} sx={{ fontSize: 13 }}>
                        {p ? (
                          <Tooltip title={p.title} placement="top">
                            <Chip label={p.title} size="small" sx={{ maxWidth: 200, fontSize: 11, fontWeight: 600, bgcolor: "#ede9fe", color: "#5b21b6" }} />
                          </Tooltip>
                        ) : "—"}
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
}

// ════════════════════════════════════════════════════════════════════
// STOK PANELİ — Kart / Liste görünümü + bilet sahipleri drawer
// ════════════════════════════════════════════════════════════════════
function StockPanel({ products, selectedId, onSelect, onToggleClosed }) {
  const [view, setView]         = useState("card"); // "card" | "list"
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [search, setSearch]     = useState("");

  // ── Durum hesapla ──
  const getStatus = (p) => {
    if (p.closed) return "closed";
    const r = p.total > 0 ? p.sold / p.total : 0;
    if (r >= 1)    return "full";
    if (r >= 0.8)  return "low";
    return "ok";
  };
  const statusLabel = { closed: "Kapalı", full: "Dolu", low: "Az kaldı", ok: "Müsait" };
  const statusColors = {
    closed: { bg: "#eeeeee",  text: "#616161" },
    full: { bg: C.redBg,    text: C.red    },
    low:  { bg: C.orangeBg, text: C.orange },
    ok:   { bg: C.greenBg,  text: C.green  },
  };
  const barColor = (s) => s === "closed" ? "#9e9e9e" : s === "full" ? C.red : s === "low" ? C.orange : C.green;
  const closeBtn = (p) => onToggleClosed && (
    <Button
      size="small"
      onClick={(e) => { e.stopPropagation(); onToggleClosed(p); }}
      sx={{ textTransform: "none", fontSize: 11, py: 0, minWidth: 0, color: p.closed ? C.green : C.red }}
    >
      {p.closed ? "Yeniden aç" : "Kapat"}
    </Button>
  );

  // ── Filtrele ──
  const q = search.toLowerCase();
  const visible = products.filter((p) => {
    const s = getStatus(p);
    const matchStatus = statusFilter === "ALL" || s === statusFilter;
    const matchSearch = !q || p.title.toLowerCase().includes(q)
      || p.moderator.toLowerCase().includes(q)
      || p.room.toLowerCase().includes(q);
    return matchStatus && matchSearch;
  });

  const handleCardClick = (id) => onSelect(selectedId === id ? null : id);

  // ── Toolbar ──
  const toolbar = (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2, flexWrap: "wrap" }}>
      <TextField
        size="small"
        placeholder="Atölye, moderatör veya salon ara…"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        sx={{ width: 280, bgcolor: C.surface }}
        InputProps={{ startAdornment: <SearchIcon sx={{ mr: 0.5, fontSize: 16, color: C.muted }} /> }}
      />

      {/* Durum filtreleri */}
      <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap" }}>
        {[
          { key: "ALL",  label: "Tümü" },
          { key: "ok",   label: "Müsait" },
          { key: "low",  label: "Az kaldı" },
          { key: "full", label: "Dolu" },
          { key: "closed", label: "Kapalı" },
        ].map(({ key, label }) => (
          <Box
            key={key}
            onClick={() => { setStatusFilter(key); onSelect(null); }}
            sx={{
              px: 1.5, py: 0.5,
              borderRadius: "20px",
              border: `0.5px solid ${statusFilter === key ? C.pink : C.border}`,
              bgcolor: statusFilter === key ? C.pink : C.surface,
              color: statusFilter === key ? "#fff" : C.muted,
              fontSize: 12,
              fontWeight: statusFilter === key ? 700 : 400,
              cursor: "pointer",
              userSelect: "none",
              transition: "all .15s",
              "&:hover": { borderColor: C.pink },
            }}
          >
            {label}
          </Box>
        ))}
      </Box>

      {/* Görünüm toggle */}
      <Box sx={{ ml: "auto", display: "flex", border: `0.5px solid ${C.border}`, borderRadius: 1.5, overflow: "hidden" }}>
        {[
          { v: "card", icon: "ViewModuleIcon" },
          { v: "list", icon: "ListIcon" },
        ].map(({ v, icon }) => (
          <Box
            key={v}
            onClick={() => { setView(v); onSelect(null); }}
            sx={{
              px: 1.2, py: 0.6,
              cursor: "pointer",
              display: "flex", alignItems: "center",
              bgcolor: view === v ? C.pinkLight : C.surface,
              color: view === v ? C.pinkDark : C.muted,
              transition: "background .15s",
              "&:hover": { bgcolor: C.pinkLight },
            }}
          >
            {v === "card"
              ? <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/><rect x="3" y="14" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/></svg>
              : <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="18" x2="21" y2="18"/></svg>
            }
          </Box>
        ))}
      </Box>
    </Box>
  );

  if (!visible.length) {
    return (
      <Box>
        {toolbar}
        <EmptyState text="Arama veya filtreyle eşleşen atölye bulunamadı." />
      </Box>
    );
  }

  // ── Kart görünümü ──
  const cardView = (
    <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(230px, 1fr))", gap: 1.5 }}>
      {visible.map((p) => {
        const s    = getStatus(p);
        const pct  = p.total > 0 ? Math.min(100, Math.round((p.sold / p.total) * 100)) : 0;
        const isSel = selectedId === p.id;
        return (
          <Paper
            key={p.id}
            elevation={0}
            onClick={() => handleCardClick(p.id)}
            sx={{
              p: 2,
              border: isSel ? `1.5px solid ${C.pink}` : `0.5px solid ${C.border}`,
              borderRadius: 2.5,
              cursor: "pointer",
              position: "relative",
              bgcolor: isSel ? C.pinkLight : C.surface,
              transition: "border-color .15s, background .15s",
              "&:hover": { borderColor: C.pink },
            }}
          >
            {/* Durum badge */}
            <Box sx={{
              position: "absolute", top: 10, right: 10,
              px: 1, py: 0.25,
              borderRadius: "20px",
              fontSize: 11, fontWeight: 700,
              bgcolor: statusColors[s].bg,
              color: statusColors[s].text,
            }}>
              {statusLabel[s]}
            </Box>

            <Typography sx={{ fontSize: 13, fontWeight: 600, lineHeight: 1.4, pr: 7, mb: 0.5, color: C.text }}>
              {p.title}
            </Typography>
            <Box sx={{ display: "flex", gap: 1.5, mb: 1.5, flexWrap: "wrap" }}>
              <Typography sx={{ fontSize: 11, color: C.muted }}>👤 {p.moderator}</Typography>
              <Typography sx={{ fontSize: 11, color: C.muted }}>🚪 {p.room}</Typography>
              {p.start_at && (
                <Typography sx={{ fontSize: 11, color: C.muted }}>
                  🕒 {moment(p.start_at).format("DD.MM HH:mm")}
                  {p.end_at ? " – " + moment(p.end_at).format("HH:mm") : ""}
                </Typography>
              )}
            </Box>

            {/* Bar */}
            <Box sx={{ height: 6, bgcolor: "#f0f0f0", borderRadius: 3, overflow: "hidden", mb: 0.75 }}>
              <Box sx={{ height: "100%", width: `${pct}%`, bgcolor: barColor(s), borderRadius: 3, transition: "width .3s" }} />
            </Box>

            {/* Sayılar */}
            <Box sx={{ display: "flex", justifyContent: "space-between", fontSize: 12, color: C.muted }}>
              <span><strong style={{ color: C.pinkDark }}>{p.sold}</strong> satıldı</span>
              <span style={{ color: C.muted }}>{pct}%</span>
              <span><strong style={{ color: s === "full" ? C.red : C.green }}>{p.total - p.sold}</strong> kaldı / {p.total}</span>
            </Box>
            {onToggleClosed && <Box sx={{ mt: 0.75, textAlign: "right" }}>{closeBtn(p)}</Box>}
          </Paper>
        );
      })}
    </Box>
  );

  // ── Liste görünümü ──
  const listView = (
    <TableContainer component={Paper} elevation={0} sx={tablePaper}>
      <Table size="small">
        <TableHead>
          <TableRow sx={{ bgcolor: "#fafafa" }}>
            {["Atölye Adı", "Moderatör", "Salon", "Saat", "Toplam", "Satılan", "Kalan", "Doluluk", "Durum", ...(onToggleClosed ? [""] : [])].map((h, i) => (
              <TableCell key={h} sx={th}>{h}</TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {visible.map((p) => {
            const s    = getStatus(p);
            const pct  = p.total > 0 ? Math.min(100, Math.round((p.sold / p.total) * 100)) : 0;
            const rem  = p.total - p.sold;
            const isSel = selectedId === p.id;
            return (
              <TableRow
                hover key={p.id}
                onClick={() => handleCardClick(p.id)}
                sx={{ cursor: "pointer", bgcolor: isSel ? C.pinkLight : "inherit" }}
              >
                <TableCell sx={{ fontSize: 13, maxWidth: 220 }}>
                  <Tooltip title={p.title} placement="top">
                    <span style={{ fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", display: "block" }}>
                      {p.title}
                    </span>
                  </Tooltip>
                </TableCell>
                <TableCell sx={{ fontSize: 12, color: C.muted, whiteSpace: "nowrap" }}>{p.moderator}</TableCell>
                <TableCell sx={{ fontSize: 12, color: C.muted, whiteSpace: "nowrap" }}>{p.room}</TableCell>
                <TableCell sx={{ fontSize: 12, color: C.muted, whiteSpace: "nowrap" }}>
                  {p.start_at
                    ? moment(p.start_at).format("DD.MM HH:mm") + (p.end_at ? " – " + moment(p.end_at).format("HH:mm") : "")
                    : "—"}
                </TableCell>
                <TableCell sx={{ textAlign: "center", fontWeight: 600 }}>{p.total}</TableCell>
                <TableCell sx={{ textAlign: "center", fontWeight: 700, color: C.pinkDark }}>{p.sold}</TableCell>
                <TableCell sx={{ textAlign: "center", fontWeight: 700, color: s === "full" ? C.red : C.green }}>{rem}</TableCell>
                <TableCell sx={{ minWidth: 120 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Box sx={{ flex: 1, height: 6, bgcolor: "#f0f0f0", borderRadius: 3, overflow: "hidden" }}>
                      <Box sx={{ height: "100%", width: `${pct}%`, bgcolor: barColor(s), borderRadius: 3 }} />
                    </Box>
                    <Typography sx={{ fontSize: 11, color: C.muted, minWidth: 28 }}>{pct}%</Typography>
                  </Box>
                </TableCell>
                <TableCell>
                  <Box sx={{
                    display: "inline-block", px: 1, py: 0.25,
                    borderRadius: "20px", fontSize: 11, fontWeight: 700,
                    bgcolor: statusColors[s].bg, color: statusColors[s].text,
                  }}>
                    {statusLabel[s]}
                  </Box>
                </TableCell>
                {onToggleClosed && <TableCell>{closeBtn(p)}</TableCell>}
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );

  return (
    <Box>
      {toolbar}
      {view === "card" ? cardView : listView}
    </Box>
  );
}

// ── Yeni Sezon Modal ─────────────────────────────────────────────────
function CreateSeasonModal({ open, onClose, showSnack }) {
  const [allSeasons, setAllSeasons]     = useState([]);
  const [sourceId, setSourceId]         = useState("");
  const [newId, setNewId]               = useState("");
  const [loading, setLoading]           = useState(false);
  const [fetching, setFetching]         = useState(false);
  const [error, setError]               = useState("");

  useEffect(() => {
    if (!open) return;
    setFetching(true);
    setError(""); setNewId("");
    getAllSeasons()
      .then((seasons) => {
        const sorted = [...seasons].sort((a, b) => b.id.localeCompare(a.id));
        setAllSeasons(sorted);
        if (sorted.length) setSourceId(sorted[0].id);
      })
      .catch((e) => setError("Sezonlar yüklenemedi: " + e.message))
      .finally(() => setFetching(false));
  }, [open]);

  const handleCreate = async () => {
    const id = newId.trim();
    if (!id || !sourceId) { setError("Tüm alanları doldurun."); return; }
    if (!/^[a-z0-9_]+$/.test(id)) { setError("Yalnızca küçük harf, rakam ve alt çizgi. Örn: season_2026"); return; }
    if (allSeasons.find((s) => s.id === id)) { setError("Bu ID zaten var."); return; }
    setError(""); setLoading(true);
    try {
      await cloneSeasonById(sourceId, id);
      showSnack(`"${id}" oluşturuldu! Firestore'da is_active ayarlamayı unutmayın.`, "success");
      onClose();
    } catch (e) {
      setError("Oluşturulamadı: " + e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ bgcolor: C.pink, color: "#fff", fontWeight: 800, display: "flex", alignItems: "center", gap: 1 }}>
        <ContentCopyIcon fontSize="small" /> Yeni Sezon Oluştur
      </DialogTitle>
      <DialogContent sx={{ pt: 3 }}>
        {fetching ? (
          <Box sx={{ textAlign: "center", py: 4 }}><CircularProgress sx={{ color: C.pink }} /></Box>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5, mt: 1 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Kopyalanacak Sezon</InputLabel>
              <Select value={sourceId} label="Kopyalanacak Sezon" onChange={(e) => setSourceId(e.target.value)}>
                {allSeasons.map((s) => (
                  <MenuItem key={s.id} value={s.id}>
                    {s.id}
                    {s.is_active && <Chip label="Aktif" size="small" sx={{ ml: 1, bgcolor: C.pinkLight, color: C.pinkDark, fontSize: 11, fontWeight: 700 }} />}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              label="Yeni Sezon ID"
              placeholder="season_2026"
              size="small"
              fullWidth
              value={newId}
              onChange={(e) => { setNewId(e.target.value); setError(""); }}
              helperText="Küçük harf, rakam, alt çizgi. Örn: season_2026"
            />

            <Box sx={{ bgcolor: "#fffbeb", border: "1px solid #fde68a", borderRadius: 2, p: 2, fontSize: 13, color: "#92400e" }}>
              <strong>Kopyalanır:</strong> Tüm ürünler, fiyatlar ve ayarlar.<br />
              <strong>Sıfırlanır:</strong> Stoklar → total_stock değerine döner. <code>is_active</code> kapalı başlar.
            </Box>

            {error && <Alert severity="error" sx={{ fontSize: 13 }}>{error}</Alert>}
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={onClose} color="inherit" disabled={loading}>İptal</Button>
        <Button
          onClick={handleCreate}
          variant="contained"
          disabled={loading || fetching}
          sx={btnPink}
          startIcon={loading ? <CircularProgress size={14} color="inherit" /> : <ContentCopyIcon />}
        >
          {loading ? "Oluşturuluyor…" : "Oluştur"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ════════════════════════════════════════════════════════════════════
// KUPONLAR TAB
// ════════════════════════════════════════════════════════════════════
function CouponsTab({ activeSeason, showSnack }) {
  const [email, setEmail]       = useState("");
  const [amount, setAmount]     = useState("");
  const [creating, setCreating] = useState(false);

  const [coupons, setCoupons]   = useState([]);
  const [loading, setLoading]   = useState(true);
  const [deletingId, setDeletingId] = useState(null);

  const emailValid  = /^\S+@\S+\.\S+$/.test(email.trim());
  const amountValid = Number(amount) > 0;

  const COUPON_LIMIT = 100;
  const totalCount = coupons.length;
  const limitReached = totalCount >= COUPON_LIMIT;

  const loadCoupons = useCallback(async () => {
    if (!activeSeason?.id) return;
    setLoading(true);
    try {
      const data = await getAllCoupons(activeSeason.id);
      data.sort((a, b) => (b.created_at?.seconds ?? 0) - (a.created_at?.seconds ?? 0));
      setCoupons(data);
    } catch (e) {
      showSnack("Kuponlar yüklenemedi: " + e.message, "error");
    } finally {
      setLoading(false);
    }
  }, [activeSeason?.id]);

  useEffect(() => { loadCoupons(); }, [loadCoupons]);

  const handleCreate = async () => {
    const trimmedEmail = email.trim().toLowerCase();
    if (!emailValid || !amountValid) return;
    setCreating(true);
    try {
      const existingUser = await findUserByEmail(trimmedEmail);
      if (!existingUser) {
        showSnack("Uyarı: bu e-postayla kayıtlı bir kullanıcı bulunamadı. Kupon yine de oluşturuldu.", "warning");
      }
      await createCouponByEmail(trimmedEmail, activeSeason.id, { amount: Number(amount) });
      showSnack(`${trimmedEmail} için ₺${amount} kupon oluşturuldu.`, "success");
      setEmail(""); setAmount("");
      loadCoupons();
    } catch (e) {
      showSnack("Kupon oluşturulamadı: " + e.message, "error");
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (coupon) => {
    if (!window.confirm(`${coupon.email} kuponu (₺${coupon.amount}) silinsin mi?`)) return;
    setDeletingId(coupon.id);
    try {
      await deleteCoupon(coupon.id);
      showSnack("Kupon silindi.", "success");
      setCoupons((prev) => prev.filter((c) => c.id !== coupon.id));
    } catch (e) {
      showSnack("Silinemedi: " + e.message, "error");
    } finally {
      setDeletingId(null);
    }
  };

  if (!activeSeason) return <EmptyState text="Aktif sezon bulunamadı." />;

  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 3 }}>
        <Typography variant="h6" fontWeight={800}>Kuponlar</Typography>
        <Chip label={activeSeason.id} size="small" sx={{ bgcolor: C.pinkLight, color: C.pinkDark, fontWeight: 700 }} />
        <Chip
          label={`${totalCount} / ${COUPON_LIMIT} kişi`}
          size="small"
          sx={{
            bgcolor: limitReached ? C.redBg : C.greenBg,
            color: limitReached ? C.red : C.green,
            fontWeight: 700,
          }}
        />
      </Box>

      {/* Kupon oluşturma formu */}
      <Paper elevation={0} sx={{ p: 2.5, border: `1px solid ${C.border}`, borderRadius: 2.5, mb: 3 }}>
        <Typography sx={{ fontSize: 13, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: 0.6, mb: 1.5 }}>
          Yeni Kupon Oluştur
        </Typography>
        <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", alignItems: "flex-start" }}>
          <TextField
            size="small"
            label="Kullanıcı E-postası"
            placeholder="ornek@eposta.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            error={email.length > 0 && !emailValid}
            sx={{ width: 280 }}
          />
          <TextField
            size="small"
            label="İndirim Tutarı (₺)"
            type="number"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            error={amount.length > 0 && !amountValid}
            sx={{ width: 160 }}
          />
          <Button
            variant="contained"
            onClick={handleCreate}
            disabled={creating || !emailValid || !amountValid || limitReached}
            startIcon={creating ? <CircularProgress size={14} color="inherit" /> : <LocalOfferIcon />}
            sx={btnPink}
          >
            {creating ? "Oluşturuluyor…" : "Kupon Oluştur"}
          </Button>
        </Box>
        <Typography sx={{ fontSize: 12, color: limitReached ? C.red : C.muted, mt: 1.5, fontWeight: limitReached ? 700 : 400 }}>
          {limitReached
            ? `Kontenjan doldu: bu sezon için en fazla ${COUPON_LIMIT} kişiye indirimli bilet kuponu tanımlanabilir.`
            : `Kupon sadece girilen e-postaya sahip kullanıcı tarafından, tek seferlik kullanılabilir. Ödeme tamamlandığında otomatik olarak "kullanıldı" işaretlenir. (${totalCount}/${COUPON_LIMIT} kişi kullanıldı)`}
        </Typography>
      </Paper>

      {/* Kupon listesi */}
      {loading ? (
        <Box sx={{ textAlign: "center", py: 6 }}><CircularProgress sx={{ color: C.pink }} /></Box>
      ) : coupons.length === 0 ? (
        <EmptyState icon={<LocalOfferIcon sx={{ fontSize: 40, color: C.muted }} />} text="Bu sezon için henüz kupon oluşturulmamış." />
      ) : (
        <TableContainer component={Paper} elevation={0} sx={tablePaper}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: "#fafafa" }}>
                <TableCell sx={th}>E-posta</TableCell>
                <TableCell sx={th}>Tutar</TableCell>
                <TableCell sx={th}>Durum</TableCell>
                <TableCell sx={th}>Oluşturulma</TableCell>
                <TableCell sx={th}></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {coupons.map((c) => (
                <TableRow hover key={c.id}>
                  <TableCell sx={{ fontSize: 13 }}>{c.email}</TableCell>
                  <TableCell sx={{ fontSize: 13, fontWeight: 700, color: C.pinkDark }}>₺{c.amount}</TableCell>
                  <TableCell>
                    {c.is_used ? (
                      <Chip label="Kullanıldı" size="small" sx={{ bgcolor: C.greenBg, color: C.green, fontWeight: 700 }} />
                    ) : (
                      <Chip label="Kullanılmadı" size="small" sx={{ bgcolor: C.orangeBg, color: C.orange, fontWeight: 700 }} />
                    )}
                  </TableCell>
                  <TableCell sx={{ fontSize: 12, color: C.muted }}>
                    {c.created_at?.seconds ? new Date(c.created_at.seconds * 1000).toLocaleDateString("tr-TR", { timeZone: "Europe/Istanbul" }) : "—"}
                  </TableCell>
                  <TableCell>
                    {!c.is_used && (
                      <Tooltip title="Kuponu Sil">
                        <Box
                          onClick={() => handleDelete(c)}
                          sx={{ cursor: "pointer", color: C.red, display: "inline-flex", alignItems: "center", opacity: deletingId === c.id ? 0.4 : 1 }}
                        >
                          <DeleteOutlineIcon fontSize="small" />
                        </Box>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
}

// ════════════════════════════════════════════════════════════════════
// İSTATİSTİKLER TAB
// ════════════════════════════════════════════════════════════════════
function StatsTab({ activeSeason }) {
  const [stats, setStats]   = useState(null);
  const [loading, setLoading] = useState(true);
  const [openDay, setOpenDay] = useState(null); // açık günün tarihi (YYYY-MM-DD)

  useEffect(() => {
    getUserStatistics()
      .then(setStats)
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <Box sx={{ textAlign: "center", py: 6 }}><CircularProgress sx={{ color: C.pink }} /></Box>;
  if (!stats)  return <EmptyState text="İstatistikler yüklenemedi." />;

  const summaryCards = [
    { label: "Bugün Kaydolan",  value: stats.todayUsers,    color: C.blue,   bg: C.blueBg },
    { label: "Toplam Kullanıcı",value: stats.totalUsers,    color: C.text,   bg: "#f3f4f6" },
    { label: "Bugün Satılan Kongre", value: stats.kongreToday,        color: C.orange,  bg: C.orangeBg },
    { label: "Bugün Satılan Atölye", value: stats.workshopToday,      color: "#7c3aed", bg: "#ede9fe" },
    { label: "Toplam Satılan Kongre", value: stats.kongreKatilimCount, color: C.orange,  bg: C.orangeBg },
    { label: "Toplam Satılan Atölye", value: stats.digerUrunlerCount,  color: "#7c3aed", bg: "#ede9fe" },
  ];

  return (
    <Box>
      <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 2, mb: 4 }}>
        {summaryCards.map((card) => (
          <Paper key={card.label} elevation={0} sx={{ p: 2, border: `1px solid ${C.border}`, borderRadius: 2.5, bgcolor: card.bg }}>
            <Typography sx={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: 0.6 }}>
              {card.label}
            </Typography>
            <Typography sx={{ fontSize: 26, fontWeight: 800, color: card.color, mt: 0.5 }}>{card.value ?? "—"}</Typography>
          </Paper>
        ))}
      </Box>

      {stats.dailyStats?.length > 0 && (
        <>
          <Typography sx={{ fontWeight: 700, mb: 1.5, fontSize: 14 }}>Gün Gün Kayıt / Satılan Kongre / Satılan Atölye</Typography>
          {/* Başlık ayrı tabloda, sadece satırlar kayar (başlığın altından satır görünmesin) */}
          <Paper elevation={0} sx={{ ...tablePaper, mb: 4 }}>
            <Table size="small" sx={{ tableLayout: "fixed" }}>
              <DailyCols />
              <TableHead>
                <TableRow sx={{ bgcolor: "#fafafa" }}>
                  <TableCell sx={th} />
                  {["Tarih", "Kayıt Olan", "Satılan Kongre", "Satılan Atölye", "Günlük Tutar"].map((h) => (
                    <TableCell key={h} sx={th}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
            </Table>
            <Box sx={{ maxHeight: 400, overflowY: "auto" }}>
            <Table size="small" sx={{ tableLayout: "fixed" }}>
              <DailyCols />
              <TableBody>
                {stats.dailyStats.map((d) => {
                  const isOpen = openDay === d.date;
                  const hasPurchases = d.purchases.length > 0;
                  return (
                    <React.Fragment key={d.date}>
                      <TableRow
                        hover
                        onClick={hasPurchases ? () => setOpenDay(isOpen ? null : d.date) : undefined}
                        sx={{ cursor: hasPurchases ? "pointer" : "default", "& > td": { borderBottom: isOpen ? "none" : undefined } }}
                      >
                        <TableCell sx={{ px: 1 }}>
                          {hasPurchases && (
                            <KeyboardArrowDownIcon
                              sx={{ fontSize: 20, color: C.muted, transition: "transform 0.2s", transform: isOpen ? "rotate(180deg)" : "none" }}
                            />
                          )}
                        </TableCell>
                        <TableCell sx={{ fontSize: 13 }}>{moment(d.date).format("DD.MM.YYYY")}</TableCell>
                        <TableCell sx={{ fontSize: 13, fontWeight: 700, color: C.blue }}>{d.users}</TableCell>
                        <TableCell sx={{ fontSize: 13, fontWeight: 700, color: C.orange }}>{d.kongreSold}</TableCell>
                        <TableCell sx={{ fontSize: 13, fontWeight: 700, color: "#7c3aed" }}>{d.workshopSold}</TableCell>
                        <TableCell sx={{ fontSize: 13, fontWeight: 700, color: C.pinkDark }}>₺{d.revenue.toLocaleString("tr-TR")}</TableCell>
                      </TableRow>

                      {/* Açılır detay: o gün kim ne almış */}
                      {hasPurchases && (
                        <TableRow>
                          <TableCell colSpan={6} sx={{ p: 0, borderBottom: isOpen ? undefined : "none" }}>
                            <Collapse in={isOpen} unmountOnExit>
                              <Box sx={{ px: 2, py: 1.5, bgcolor: "#fafafa" }}>
                                {d.purchases.map((p, i) => (
                                  <Box key={p.id} sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 1, py: 0.8, borderTop: i ? `1px dashed ${C.border}` : "none" }}>
                                    <Typography sx={{ fontSize: 12, color: C.muted, minWidth: 42 }}>{moment(p.paidMs).format("HH:mm")}</Typography>
                                    <Typography sx={{ fontSize: 13, minWidth: 220 }}>{p.email}</Typography>
                                    {p.byGroup && (
                                      <Chip
                                        label={`Toplu ödeme · Ödeyen: ${p.paidByEmail ?? "—"}`}
                                        size="small"
                                        sx={{ height: 18, fontSize: 10.5, fontWeight: 700, bgcolor: C.greenBg, color: C.green }}
                                      />
                                    )}
                                    {p.isGroupPayer && (
                                      <Chip label="Toplu ödemeyi yapan" size="small" sx={{ height: 18, fontSize: 10.5, fontWeight: 700, bgcolor: C.greenBg, color: C.green }} />
                                    )}
                                    {p.products.map((pr) => (
                                      <Chip
                                        key={pr.id}
                                        label={pr.isKongre ? "Kongre" : pr.title}
                                        size="small"
                                        sx={{
                                          height: 20,
                                          fontSize: 11,
                                          fontWeight: 600,
                                          bgcolor: pr.isKongre ? C.orangeBg : "#ede9fe",
                                          color: pr.isKongre ? C.orange : "#5b21b6",
                                        }}
                                      />
                                    ))}
                                    <Box sx={{ flex: 1 }} />
                                    <Typography sx={{ fontSize: 13, fontWeight: 700, color: C.pinkDark }}>₺{p.price.toLocaleString("tr-TR")}</Typography>
                                  </Box>
                                ))}
                              </Box>
                            </Collapse>
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                  );
                })}
              </TableBody>
            </Table>
            </Box>
          </Paper>
        </>
      )}

      {stats.seasonProducts?.length > 0 && (
        <>
          <Typography sx={{ fontWeight: 700, mb: 1.5, fontSize: 14 }}>Ürün Stok Durumu</Typography>
          <TableContainer component={Paper} elevation={0} sx={tablePaper}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: "#fafafa" }}>
                  {["Ürün Adı", "Salon", "Kalan Stok", "Toplam Stok"].map((h) => (
                    <TableCell key={h} sx={th}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {/* En az stok kalan en üstte */}
                {[...stats.seasonProducts]
                  .sort((a, b) => (a.stock ?? Infinity) - (b.stock ?? Infinity) || (a.title ?? "").localeCompare(b.title ?? "", "tr"))
                  .map((p, i) => (
                  <TableRow hover key={i}>
                    <TableCell sx={{ fontSize: 13 }}>{p.title}</TableCell>
                    <TableCell sx={{ fontSize: 13, color: C.muted }}>{p.room}</TableCell>
                    <TableCell sx={{ fontWeight: 700, color: p.stock === 0 ? C.red : C.green }}>{p.stock}</TableCell>
                    <TableCell sx={{ fontSize: 13 }}>{p.total_stock}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}
    </Box>
  );
}

// ════════════════════════════════════════════════════════════════════
// KONTROL TAB
// ════════════════════════════════════════════════════════════════════
function ControlTab({ activeSeason }) {
  const [data, setData]         = useState(null);
  const [loading, setLoading]   = useState(true);
  const [view, setView]         = useState("multi"); // "multi" | "group"
  const [search, setSearch]     = useState("");
  const [workshop, setWorkshop] = useState("");
  const [minCount, setMinCount] = useState(2);
  const [open, setOpen]         = useState({}); // açık satırlar: { "<view>:<key>": true }

  useEffect(() => {
    getControlStats(activeSeason?.id)
      .then(setData)
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [activeSeason?.id]);

  if (loading) return <Box sx={{ textAlign: "center", py: 6 }}><CircularProgress sx={{ color: C.pink }} /></Box>;
  if (!data)   return <EmptyState text="Kontrol verileri yüklenemedi." />;

  const wTime = (w) =>
    w.start_at
      ? moment(w.start_at).format("DD.MM HH:mm") + (w.end_at ? "–" + moment(w.end_at).format("HH:mm") : "")
      : "";
  const wLabel = (w) => (wTime(w) ? `${wTime(w)} · ${w.title}` : w.title);

  const q = search.trim().toLocaleLowerCase("tr");
  const match = (email, ws) =>
    !q || email.toLocaleLowerCase("tr").includes(q) || ws.some((w) => wLabel(w).toLocaleLowerCase("tr").includes(q));
  const hasWorkshop = (ws) => !workshop || ws.some((w) => w.title === workshop);

  const multiRows = data.multiWorkshopUsers.filter(
    (u) => u.count >= minCount && hasWorkshop(u.workshops) && match(u.email, u.workshops)
  );
  const groupRows = data.groupPayers.filter((p) =>
    p.members.some((m) => hasWorkshop(m.workshops)) &&
    (match(p.email, []) || p.members.some((m) => match(m.email, m.workshops)))
  );
  const rows = view === "multi" ? multiRows : groupRows;

  const rowId = (r) => `${view}:${r.key}`;
  const allOpen = rows.length > 0 && rows.every((r) => open[rowId(r)]);
  const toggleRow = (r) => setOpen((o) => ({ ...o, [rowId(r)]: !o[rowId(r)] }));
  const toggleAll = () =>
    setOpen((o) => {
      const next = { ...o };
      rows.forEach((r) => { next[rowId(r)] = !allOpen; });
      return next;
    });

  const cards = [
    { id: "multi", label: "Birden Çok Atölye Alan",          value: data.multiWorkshopUsers.length, color: C.blue,  bg: C.blueBg,  ring: C.blue },
    { id: "group", label: "Toplu Ödeme Yapan (Çok Kişilik)", value: data.groupPayers.length,        color: C.green, bg: C.greenBg, ring: C.green },
  ];

  const workshopChips = (ws, empty = "Atölye yok") =>
    ws.length === 0 ? (
      empty ? <Typography sx={{ fontSize: 12, color: C.muted }}>{empty}</Typography> : null
    ) : (
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.6 }}>
        {ws.map((w) => (
          <Chip
            key={w.id}
            label={wLabel(w)}
            size="small"
            onClick={() => setWorkshop((cur) => (cur === w.title ? "" : w.title))}
            sx={{
              fontSize: 11.5,
              fontWeight: 600,
              bgcolor: workshop === w.title ? "#7c3aed" : "#ede9fe",
              color: workshop === w.title ? "#fff" : "#5b21b6",
              cursor: "pointer",
            }}
          />
        ))}
      </Box>
    );

  const exportExcel = () => {
    const out =
      view === "multi"
        ? multiRows.map((u) => ({
            "E-POSTA": u.email,
            "ATÖLYE ADEDİ": u.count,
            "ATÖLYELER": u.workshops.map(wLabel).join(" | "),
            "BAŞKASI ÖDEMİŞ": u.boughtByOthers ? "Evet" : "",
          }))
        : groupRows.flatMap((p) =>
            p.members.map((m) => ({
              "ÖDEYEN": p.email,
              "KİŞİ": m.email,
              "ÖDEYENİN KENDİSİ": m.isPayer ? "Evet" : "",
              "KONGRE": m.hasKongre ? "Evet" : "",
              "ATÖLYELER": m.workshops.map(wLabel).join(" | "),
            }))
          );
    const ws = utils.json_to_sheet(out);
    ws["!cols"] = Array(5).fill({ wch: 35 });
    const wb = utils.book_new();
    utils.book_append_sheet(wb, ws, "Kontrol");
    writeFileXLSX(wb, view === "multi" ? "Birden_Cok_Atolye.xlsx" : "Toplu_Odeme.xlsx");
  };

  return (
    <Box>
      {/* Seçilebilir özet kartları */}
      <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 2, mb: 3 }}>
        {cards.map((card) => {
          const active = view === card.id;
          return (
            <Paper
              key={card.id}
              elevation={0}
              onClick={() => { setView(card.id); setSearch(""); setWorkshop(""); }}
              sx={{
                p: 2,
                cursor: "pointer",
                borderRadius: 2.5,
                bgcolor: card.bg,
                border: `2px solid ${active ? card.ring : C.border}`,
                transition: "all 0.15s",
                opacity: active ? 1 : 0.7,
                "&:hover": { opacity: 1, transform: "translateY(-1px)" },
              }}
            >
              <Typography sx={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: 0.6 }}>
                {card.label}
              </Typography>
              <Typography sx={{ fontSize: 26, fontWeight: 800, color: card.color, mt: 0.5 }}>{card.value}</Typography>
            </Paper>
          );
        })}
      </Box>

      {/* Filtre çubuğu */}
      <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 1.5, mb: 2 }}>
        <TextField
          size="small"
          placeholder="E-posta veya atölye ara…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{ startAdornment: <SearchIcon sx={{ mr: 0.5, fontSize: 18, color: C.muted }} /> }}
          sx={{ minWidth: 240, bgcolor: "#fff" }}
        />
        <FormControl size="small" sx={{ minWidth: 220, bgcolor: "#fff" }}>
          <InputLabel>Atölyeye göre</InputLabel>
          <Select label="Atölyeye göre" value={workshop} onChange={(e) => setWorkshop(e.target.value)}>
            <MenuItem value=""><em>Tümü</em></MenuItem>
            {data.workshopOptions.map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
          </Select>
        </FormControl>
        {view === "multi" && (
          <Box sx={{ display: "flex", gap: 0.8, alignItems: "center" }}>
            {[2, 3, 4].map((n) => (
              <Chip
                key={n}
                label={`${n}+ atölye`}
                onClick={() => setMinCount(n)}
                sx={{
                  fontWeight: 700,
                  bgcolor: minCount === n ? C.blue : "#fff",
                  color: minCount === n ? "#fff" : C.text,
                  border: `1px solid ${minCount === n ? C.blue : C.border}`,
                }}
              />
            ))}
          </Box>
        )}
        <Box sx={{ flex: 1 }} />
        <Typography sx={{ fontSize: 12.5, color: C.muted }}>{rows.length} kayıt</Typography>
        <Button size="small" onClick={toggleAll} disabled={rows.length === 0 || view === "multi"}>
          {allOpen ? "Tümünü kapat" : "Tümünü aç"}
        </Button>
        <Button size="small" startIcon={<DownloadIcon />} onClick={exportExcel} disabled={rows.length === 0}>
          Excel
        </Button>
      </Box>

      {rows.length === 0 ? (
        <EmptyState text="Filtreye uyan kayıt yok." />
      ) : (
        <TableContainer component={Paper} elevation={0} sx={tablePaper}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: "#fafafa" }}>
                {view === "group" && <TableCell sx={{ ...th, width: 40 }} />}
                {(view === "multi"
                  ? ["E-posta", "Adet", "Aldığı Atölyeler"]
                  : ["Ödeyen", "Ödeme", "Kapsanan Kişi"]
                ).map((h) => <TableCell key={h} sx={th}>{h}</TableCell>)}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((r) => {
                const isOpen = !!open[rowId(r)];
                return (
                  <React.Fragment key={rowId(r)}>
                    <TableRow
                      hover
                      onClick={view === "group" ? () => toggleRow(r) : undefined}
                      sx={{ cursor: view === "group" ? "pointer" : "default", "& > td": { borderBottom: view === "group" && isOpen ? "none" : undefined } }}
                    >
                      {view === "group" && (
                        <TableCell sx={{ px: 1 }}>
                          <KeyboardArrowDownIcon
                            sx={{ fontSize: 20, color: C.muted, transition: "transform 0.2s", transform: isOpen ? "rotate(180deg)" : "none" }}
                          />
                        </TableCell>
                      )}
                      <TableCell sx={{ fontSize: 13 }}>
                        {r.email}
                        {view === "multi" && r.boughtByOthers && (
                          <Chip label="Başkası ödemiş" size="small" sx={{ ml: 1, height: 18, fontSize: 10.5, bgcolor: C.greenBg, color: C.green, fontWeight: 700 }} />
                        )}
                      </TableCell>
                      {view === "multi" ? (
                        <>
                          <TableCell sx={{ fontSize: 13, fontWeight: 800, color: C.blue }}>{r.count}</TableCell>
                          <TableCell>{workshopChips(r.workshops)}</TableCell>
                        </>
                      ) : (
                        <>
                          <TableCell sx={{ fontSize: 13 }}>{r.groupCount}</TableCell>
                          <TableCell sx={{ fontSize: 13, fontWeight: 800, color: C.green }}>{r.memberCount}</TableCell>
                        </>
                      )}
                    </TableRow>

                    {/* Açılır detay: toplu ödemede kişi + atölyeleri */}
                    {view === "group" && (
                      <TableRow>
                        <TableCell colSpan={4} sx={{ p: 0, borderBottom: isOpen ? undefined : "none" }}>
                          <Collapse in={isOpen} unmountOnExit>
                            <Box sx={{ px: 2, py: 1.5, bgcolor: "#fafafa" }}>
                              {r.members.map((m, i) => (
                                <Box key={i} sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 1, py: 0.8, borderTop: i ? `1px dashed ${C.border}` : "none" }}>
                                  <Typography sx={{ fontSize: 13, minWidth: 220 }}>{m.email}</Typography>
                                  {m.isPayer && <Chip label="Ödeyen" size="small" sx={{ height: 18, fontSize: 10.5, fontWeight: 700 }} />}
                                  {m.hasKongre && <Chip label="Kongre" size="small" sx={{ height: 18, fontSize: 10.5, fontWeight: 700, bgcolor: C.orangeBg, color: C.orange }} />}
                                  {workshopChips(m.workshops, "")}
                                </Box>
                              ))}
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
}

// ════════════════════════════════════════════════════════════════════
// MAILING İÇİN E-POSTALAR TAB
// ════════════════════════════════════════════════════════════════════
function NonBuyersTab({ activeSeason, showSnack }) {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!activeSeason?.id) return;
    const products = activeSeason.products ?? {};
    const isKongre = (pid) => products[pid]?.product_type === 1 || pid.includes("kongre_katilim");
    const norm = (email) => (email ? email.trim().toLowerCase() : "");

    Promise.all([getAllUsers(), getPaidReceiptsBySeason(activeSeason.id)])
      .then(([users, receipts]) => {
        // Bu sezon ödenmiş ürünler: hem user_id hem e-posta anahtarıyla (toplu ödemeler için)
        const bought = {};
        receipts.forEach((r) => {
          const pids = Object.keys(r.receipt_products ?? {}).filter((pid) => r.receipt_products[pid]);
          [r.user_id, norm(r.email)].filter(Boolean).forEach((k) => {
            bought[k] = (bought[k] ?? []).concat(pids);
          });
        });

        const rows = users
          .filter((u) => u.role !== "admin" && u.email)
          .map((u) => {
            const pids = [...(bought[u.id] ?? []), ...(bought[norm(u.email)] ?? [])];
            return {
              email: u.email.trim(),
              registration_season: u.registration_season,
              hasKongre: pids.some(isKongre),
              workshopCount: new Set(pids.filter((pid) => !isKongre(pid))).size,
            };
          });
        setData(rows);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [activeSeason?.id]);

  if (loading) return <Box sx={{ textAlign: "center", py: 6 }}><CircularProgress sx={{ color: C.pink }} /></Box>;
  if (!data)   return <EmptyState text="Kullanıcı verileri yüklenemedi." />;

  const isNew      = (u) => u.registration_season === activeSeason.id;
  const buysNone   = (u) => !u.hasKongre && u.workshopCount === 0;
  const kongreOnly = (u) => u.hasKongre && u.workshopCount === 0;

  const lists = [
    { id: "none",          label: "Kayıtlı, Kongre ve Atölye Almayan",      filter: buysNone,                          color: C.pink,   bg: C.pinkLight, file: "Kongre_ve_Atolye_Almayanlar" },
    { id: "kongreOnly",    label: "Kongre Alıp Atölye Almayan",             filter: kongreOnly,                        color: C.orange, bg: C.orangeBg,  file: "Kongre_Alip_Atolye_Almayanlar" },
    { id: "newNone",       label: "Yeni Kayıt, Kongre ve Atölye Almayan",   filter: (u) => isNew(u) && buysNone(u),    color: C.blue,   bg: C.blueBg,    file: "Yeni_Kayit_Kongre_ve_Atolye_Almayanlar" },
    { id: "newKongreOnly", label: "Yeni Kayıt, Kongre Alıp Atölye Almayan", filter: (u) => isNew(u) && kongreOnly(u),  color: C.green,  bg: C.greenBg,   file: "Yeni_Kayit_Kongre_Alip_Atolye_Almayanlar" },
  ].map((l) => ({ ...l, emails: Array.from(new Set(data.filter(l.filter).map((u) => u.email))) }));

  const copyEmails = async (list) => {
    try {
      await navigator.clipboard.writeText(list.emails.join(", "));
      showSnack(`${list.emails.length} e-posta kopyalandı.`, "success");
    } catch {
      showSnack("E-postalar kopyalanamadı.", "error");
    }
  };

  const exportExcel = (list) => {
    const ws = utils.json_to_sheet(list.emails.map((email) => ({ "E-POSTA": email })));
    ws["!cols"] = [{ wch: 40 }];
    const wb = utils.book_new();
    utils.book_append_sheet(wb, ws, "Mail Listesi");
    writeFileXLSX(wb, `${list.file}.xlsx`);
  };

  return (
    <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: 2 }}>
      {lists.map((list) => (
        <Paper
          key={list.id}
          elevation={0}
          sx={{ p: 2, borderRadius: 2.5, bgcolor: list.bg, border: `1px solid ${C.border}`, display: "flex", flexDirection: "column" }}
        >
          <Typography sx={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: 0.6 }}>
            {list.label}
          </Typography>
          <Typography sx={{ fontSize: 26, fontWeight: 800, color: list.color, mt: 0.5, mb: 1.5 }}>{list.emails.length}</Typography>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: "auto" }}>
            <Button size="small" startIcon={<ContentCopyIcon />} onClick={() => copyEmails(list)} disabled={list.emails.length === 0}>
              E-postaları kopyala
            </Button>
            <Button size="small" startIcon={<DownloadIcon />} onClick={() => exportExcel(list)} disabled={list.emails.length === 0}>
              Excel
            </Button>
          </Box>
        </Paper>
      ))}
    </Box>
  );
}

// ════════════════════════════════════════════════════════════════════
// ATÖLYELER TAB
// ════════════════════════════════════════════════════════════════════
// Dosya / sayfa adı için güvenli metin (Excel sayfa adı en fazla 31 karakter, []:*?/\ içeremez)
const safeName = (s) => s.replace(/[[\]:*?/\\]/g, " ").replace(/\s+/g, " ").trim();

const wsTimeOf = (w) =>
  w.start_at
    ? moment(w.start_at).format("DD.MM HH:mm") + (w.end_at ? "–" + moment(w.end_at).format("HH:mm") : "")
    : "";

const wsSheetOf = (w) => {
  // Üstte atölye bilgisi, altında katılımcı tablosu
  const ws = utils.aoa_to_sheet([
    ["ATÖLYE", w.title],
    ["MODERATÖR", w.moderator],
    ["ZAMAN", wsTimeOf(w)],
    [],
  ]);
  const header = ["ADI-SOYADI", "MESLEK", "ŞEHİR", "TELEFON"];
  const asksTc = w.requires_tc || w.requires_wifi;
  if (asksTc) header.push("TC KİMLİK NO");
  utils.sheet_add_json(
    ws,
    w.people.map((p) => ({
      "ADI-SOYADI": p.name,
      MESLEK: p.occupation,
      ŞEHİR: p.city,
      TELEFON: p.phone,
      ...(asksTc ? { "TC KİMLİK NO": p.tc_no } : {}),
    })),
    { header, origin: "A5" }
  );
  ws["!cols"] = [{ wch: 32 }, { wch: 28 }, { wch: 20 }, { wch: 16 }, { wch: 16 }];
  return ws;
};

const TC_HEADER = ["ATÖLYE", "ADI-SOYADI", "TC KİMLİK NO", "TELEFON", "E-POSTA"];
const wsTcRows = (w) =>
  w.people.map((p) => ({
    ATÖLYE: w.title,
    "ADI-SOYADI": p.name,
    "TC KİMLİK NO": p.tc_no,
    TELEFON: p.phone,
    "E-POSTA": p.email,
  }));
const wsTcSheet = (list) => {
  const ws = utils.json_to_sheet(list.flatMap(wsTcRows), { header: TC_HEADER });
  ws["!cols"] = [{ wch: 36 }, { wch: 28 }, { wch: 16 }, { wch: 16 }, { wch: 32 }];
  return ws;
};

const downloadWorkshopOne = (w) => {
  const wb = utils.book_new();
  utils.book_append_sheet(wb, wsSheetOf(w), safeName(w.title).slice(0, 31) || "Atölye");
  writeFileXLSX(wb, `${safeName(w.title).replace(/ /g, "_")}.xlsx`);
};

const downloadWorkshopTcOne = (w) => {
  const wb = utils.book_new();
  utils.book_append_sheet(wb, wsTcSheet([w]), safeName(w.title).slice(0, 31) || "Atölye");
  writeFileXLSX(wb, `TC_${safeName(w.title).replace(/ /g, "_")}.xlsx`);
};

// Verilen atölyeler: her biri ayrı sayfada
const downloadWorkshopsMany = (list) => {
  const wb = utils.book_new();
  const used = new Set();
  list.forEach((w, i) => {
    let name = safeName(w.title).slice(0, 31) || `Atölye ${i + 1}`;
    if (used.has(name)) name = `${name.slice(0, 27)} (${i + 1})`;
    used.add(name);
    utils.book_append_sheet(wb, wsSheetOf(w), name);
  });
  writeFileXLSX(wb, "Atolye_Katilimcilari.xlsx");
};

// Verilen atölyeler: ad soyad / TC / e-posta, tek sayfada
const downloadWorkshopsTcMany = (list) => {
  const wb = utils.book_new();
  utils.book_append_sheet(wb, wsTcSheet(list), "TC Listesi");
  writeFileXLSX(wb, "Atolye_TC_Listesi.xlsx");
};

// Sezondaki atölyeler + ödenmiş katılımcıları (ad, meslek, şehir, TC, e-posta)
function useWorkshopData(activeSeason) {
  const [workshops, setWorkshops] = useState(null);
  const [loading, setLoading]     = useState(true);

  useEffect(() => {
    if (!activeSeason?.id) { setLoading(false); return; }
    const products = activeSeason.products ?? {};
    const isKongre = (pid) => products[pid]?.product_type === 1 || pid.includes("kongre_katilim");
    const norm = (email) => (email ? email.trim().toLowerCase() : "");

    Promise.all([getAllUsers(), getPaidReceiptsBySeason(activeSeason.id)])
      .then(([users, receipts]) => {
        const byId = {};
        const byEmail = {};
        users.forEach((u) => {
          byId[u.id] = u;
          if (u.email) byEmail[norm(u.email)] = u;
        });

        // Atölye → katılımcılar (aynı kişi aynı atölyede bir kez)
        const attendees = {};
        receipts.forEach((r) => {
          const u = byId[r.user_id] ?? byEmail[norm(r.email)];
          const key = u?.id ?? norm(r.email) ?? r.id;
          Object.keys(r.receipt_products ?? {})
            .filter((pid) => r.receipt_products[pid] && !isKongre(pid))
            .forEach((pid) => {
              const list = (attendees[pid] ??= {});
              list[key] = {
                name: u?.name ?? "",
                occupation: u?.occupation ?? "",
                city: u?.city ?? "",
                tc_no: u?.tc_no ?? "",
                phone: u?.phone ?? r.phone ?? "",
                email: u?.email ?? r.email ?? "",
              };
            });
        });

        const startMs = (p) => (p?.start_at ? moment(p.start_at).valueOf() : Infinity);
        const rows = Object.keys(products)
          .filter((pid) => !isKongre(pid))
          .map((pid) => ({
            id: pid,
            title: products[pid]?.title ?? pid,
            moderator: products[pid]?.moderator ?? "",
            room: products[pid]?.room ?? "",
            start_at: products[pid]?.start_at ?? null,
            end_at: products[pid]?.end_at ?? null,
            requires_tc: products[pid]?.requires_tc === true,
            requires_wifi: products[pid]?.requires_wifi === true,            people: Object.values(attendees[pid] ?? {}).sort((a, b) => a.name.localeCompare(b.name, "tr")),
          }))
          .sort((a, b) => startMs(a) - startMs(b) || a.title.localeCompare(b.title, "tr"));
        setWorkshops(rows);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [activeSeason?.id]);

  return { workshops, setWorkshops, loading };
}

function WorkshopsTab({ activeSeason, showSnack }) {
  const { workshops, setWorkshops, loading } = useWorkshopData(activeSeason);

  if (loading)    return <Box sx={{ textAlign: "center", py: 6 }}><CircularProgress sx={{ color: C.pink }} /></Box>;
  if (!workshops) return <EmptyState text="Atölye verileri yüklenemedi." />;
  if (workshops.length === 0) return <EmptyState text="Bu sezonda atölye yok." />;

  const toggleRequiresTc = async (w, checked) => {
    // İyimser güncelleme; hata olursa geri al
    setWorkshops((prev) => prev.map((x) => (x.id === w.id ? { ...x, requires_tc: checked } : x)));
    try {
      await setProductRequiresTc(activeSeason.id, w.id, checked);
      showSnack(
        checked
          ? `"${w.title}" için TC Kimlik No istenecek.`
          : `"${w.title}" için TC Kimlik No istenmeyecek.`,
        "success"
      );
    } catch (e) {
      console.error(e);
      setWorkshops((prev) => prev.map((x) => (x.id === w.id ? { ...x, requires_tc: !checked } : x)));
      showSnack("Ayar kaydedilemedi.", "error");
    }
  };

  const makePdf = async (list, mode, fileName) => {
    try {
      await downloadWorkshopPdf(list, mode, fileName);
    } catch (e) {
      console.error(e);
      showSnack("PDF oluşturulamadı: " + (e.message ?? e), "error");
    }
  };
  const pdfName = (prefix, w) => `${prefix}_${safeName(w.title).replace(/ /g, "_")}.pdf`;
  const withPeople = workshops.filter((w) => w.people.length > 0);

  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: "center", mb: 2, flexWrap: "wrap", gap: 1 }}>
        <Typography sx={{ fontSize: 12.5, color: C.muted }}>
          {workshops.length} atölye · Atölye seçimi ve toplu indirmeler (Excel, TC listesi) Wifi sekmesindedir
        </Typography>
        <Box sx={{ flex: 1 }} />
        <Button size="small" startIcon={<PictureAsPdfIcon />} disabled={withPeople.length === 0}
          onClick={() => makePdf(withPeople, "door", "Kapi_Listeleri.pdf")}>
          Tüm kapı listeleri
        </Button>
        <Button size="small" startIcon={<PictureAsPdfIcon />} disabled={withPeople.length === 0}
          onClick={() => makePdf(withPeople, "sign", "Imza_Listeleri.pdf")}>
          Tüm imza listeleri
        </Button>
      </Box>

      <TableContainer component={Paper} elevation={0} sx={tablePaper}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: "#fafafa" }}>
              {["Atölye", "Moderatör", "Zaman", "Katılımcı", "TC iste", ""].map((h, i) => (
                <TableCell key={i} sx={th}>{h}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {workshops.map((w) => (
              <TableRow key={w.id} hover>
                <TableCell sx={{ fontSize: 13, fontWeight: 600 }}>{w.title}</TableCell>
                <TableCell sx={{ fontSize: 13 }}>{w.moderator || "—"}</TableCell>
                <TableCell sx={{ fontSize: 13, color: C.muted, whiteSpace: "nowrap" }}>{wsTimeOf(w) || "—"}</TableCell>
                <TableCell sx={{ fontSize: 13, fontWeight: 800, color: "#7c3aed" }}>{w.people.length}</TableCell>
                <TableCell>
                  <Tooltip title="Açıkken bu atölyeyi satın alanlardan profil sayfasında TC Kimlik No istenir">
                    <Switch
                      size="small"
                      checked={w.requires_tc}
                      onChange={(e) => toggleRequiresTc(w, e.target.checked)}
                    />
                  </Tooltip>
                </TableCell>
                <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                  <Button size="small" startIcon={<PictureAsPdfIcon />} onClick={() => makePdf([w], "door", pdfName("Kapi", w))} disabled={w.people.length === 0} sx={{ mr: 1 }}>
                    Kapı PDF
                  </Button>
                  <Button size="small" startIcon={<PictureAsPdfIcon />} onClick={() => makePdf([w], "sign", pdfName("Imza", w))} disabled={w.people.length === 0} sx={{ mr: 1 }}>
                    İmza PDF
                  </Button>
                  <Button size="small" startIcon={<DownloadIcon />} onClick={() => downloadWorkshopTcOne(w)} disabled={w.people.length === 0} sx={{ mr: 1 }}>
                    TC Excel
                  </Button>
                  <Button size="small" startIcon={<DownloadIcon />} onClick={() => downloadWorkshopOne(w)} disabled={w.people.length === 0}>
                    Moderatör Excel
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}

// ════════════════════════════════════════════════════════════════════
// WIFI TAB
// ════════════════════════════════════════════════════════════════════
function WifiTab({ activeSeason, showSnack }) {
  const { workshops, setWorkshops, loading } = useWorkshopData(activeSeason);
  const [selected, setSelected] = useState(() => new Set());

  if (!activeSeason?.id) return <EmptyState text="Aktif sezon bulunamadı." />;
  if (loading) return <Box sx={{ textAlign: "center", py: 6 }}><CircularProgress sx={{ color: C.pink }} /></Box>;
  if (!workshops) return <EmptyState text="Atölye verileri yüklenemedi." />;
  if (workshops.length === 0) return <EmptyState text="Bu sezonda atölye yok." />;

  const selectedWorkshops = workshops.filter((w) => selected.has(w.id));
  const allSelected = selectedWorkshops.length === workshops.length;
  const toggleSelect = (id) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const toggleSelectAll = () =>
    setSelected(allSelected ? new Set() : new Set(workshops.map((w) => w.id)));

  const toggleRequiresWifi = async (w, checked) => {
    // İyimser güncelleme; hata olursa geri al
    setWorkshops((prev) => prev.map((x) => (x.id === w.id ? { ...x, requires_wifi: checked } : x)));
    try {
      await setProductRequiresWifi(activeSeason.id, w.id, checked);
      showSnack(
        checked ? `"${w.title}" wifi isteyen atölye olarak işaretlendi.` : `"${w.title}" işareti kaldırıldı.`,
        "success"
      );
    } catch (e) {
      console.error(e);
      setWorkshops((prev) => prev.map((x) => (x.id === w.id ? { ...x, requires_wifi: !checked } : x)));
      showSnack("Ayar kaydedilemedi.", "error");
    }
  };

  const wifiCount = workshops.filter((w) => w.requires_wifi).length;
  // İndirme hedefi: seçim varsa seçilenler, yoksa wifi isteyen atölyeler
  const targets = selectedWorkshops.length > 0 ? selectedWorkshops : workshops.filter((w) => w.requires_wifi);
  const targetLabel = selectedWorkshops.length > 0 ? "Seçilenlerin" : "Wifi isteyenlerin";

  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: "center", mb: 2, flexWrap: "wrap", gap: 1 }}>
        <Typography sx={{ fontSize: 12.5, color: C.muted }}>
          {workshops.length} atölye · {wifiCount} wifi isteyen · {selectedWorkshops.length} seçili · Önce wifi isteyen atölyeleri işaretleyin; indirmek için atölyeleri seçin
        </Typography>
        <Box sx={{ flex: 1 }} />
        <Button
          size="small"
          startIcon={<DownloadIcon />}
          disabled={targets.length === 0}
          onClick={() => {
            downloadWorkshopsTcMany(targets);
            showSnack("TC listesi indirildi.", "success");
          }}
        >
          {targetLabel} TC listesi ({targets.length} atölye, tek sayfa)
        </Button>
        <Button
          size="small"
          startIcon={<DownloadIcon />}
          disabled={targets.length === 0}
          onClick={() => {
            downloadWorkshopsMany(targets);
            showSnack(`${targets.length} atölye indirildi.`, "success");
          }}
        >
          {targetLabel} atölyeleri indir (ayrı sayfalar)
        </Button>
      </Box>

      <TableContainer component={Paper} elevation={0} sx={tablePaper}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: "#fafafa" }}>
              <TableCell padding="checkbox" sx={{ ...th, cursor: "pointer" }} onClick={toggleSelectAll}>
                <Checkbox
                  size="small"
                  checked={allSelected}
                  indeterminate={selectedWorkshops.length > 0 && !allSelected}
                  sx={{ pointerEvents: "none" }}
                  tabIndex={-1}
                  readOnly
                />
              </TableCell>
              {["Atölye", "Moderatör", "Zaman", "Katılımcı", "Wifi istiyor"].map((h) => (
                <TableCell key={h} sx={th}>{h}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {workshops.map((w) => (
              <TableRow
                key={w.id}
                hover
                selected={selected.has(w.id)}
                onClick={() => toggleSelect(w.id)}
                sx={{ cursor: "pointer" }}
              >
                <TableCell padding="checkbox">
                  {/* Tıklama satırın onClick'ine gider; kutucuk sadece durumu gösterir */}
                  <Checkbox size="small" checked={selected.has(w.id)} sx={{ pointerEvents: "none" }} tabIndex={-1} readOnly />
                </TableCell>
                <TableCell sx={{ fontSize: 13, fontWeight: 600 }}>{w.title}</TableCell>
                <TableCell sx={{ fontSize: 13 }}>{w.moderator || "—"}</TableCell>
                <TableCell sx={{ fontSize: 13, color: C.muted, whiteSpace: "nowrap" }}>{wsTimeOf(w) || "—"}</TableCell>
                <TableCell sx={{ fontSize: 13, fontWeight: 800, color: "#7c3aed" }}>{w.people.length}</TableCell>
                <TableCell onClick={(e) => e.stopPropagation()}>
                  <Switch
                    size="small"
                    checked={w.requires_wifi}
                    onChange={(e) => toggleRequiresWifi(w, e.target.checked)}
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}

// ════════════════════════════════════════════════════════════════════
// GEÇMİŞ SEZONLAR TAB
// ════════════════════════════════════════════════════════════════════
function PastSeasonsTab({ showSnack }) {
  const [allSeasons, setAllSeasons]         = useState([]);
  const [selectedSeasonId, setSelectedSeasonId] = useState("");
  const [selectedSeason, setSelectedSeason] = useState(null);
  const [receipts, setReceipts]             = useState([]);
  const [fetching, setFetching]             = useState(true);
  const [loading, setLoading]               = useState(false);
  const [downloading, setDownloading]       = useState(false);

  // Tüm sezonları yükle
  useEffect(() => {
    getAllSeasons()
      .then((seasons) => {
        const sorted = [...seasons].sort((a, b) => b.id.localeCompare(a.id));
        setAllSeasons(sorted);
        if (sorted.length) setSelectedSeasonId(sorted[0].id);
      })
      .catch((e) => showSnack("Sezonlar yüklenemedi: " + e.message, "error"))
      .finally(() => setFetching(false));
  }, []);

  // Seçili sezondaki verileri yükle
  useEffect(() => {
    if (!selectedSeasonId || !allSeasons.length) return;
    const season = allSeasons.find((s) => s.id === selectedSeasonId) ?? null;
    setSelectedSeason(season);
    setLoading(true);
    (async () => {
      try {
        const data = await getReceiptsBySeasonId(selectedSeasonId);
        const uniqueIds = [...new Set(data.map((r) => r.user_id).filter(Boolean))];
        const nameMap = await getUserNamesByIds(uniqueIds);
        setReceipts(data.map((r) => ({ ...r, name: r.name || nameMap[r.user_id] || "" })));
      } catch (e) {
        showSnack("Veriler yüklenemedi: " + e.message, "error");
      } finally {
        setLoading(false);
      }
    })();
  }, [selectedSeasonId, allSeasons]);

  const downloadExcel = async () => {
    if (!selectedSeason) return;
    setDownloading(true);
    try {
      const productIds = Object.keys(selectedSeason.products ?? {}).sort();
      const rows = receipts.map((r) => {
        const base = {
          "ADI-SOYADI": r.name ?? "",
          "EPOSTA":     r.email ?? "",
          "TELEFON":    r.phone ?? "",
          "FİYAT":      r.price ?? 0,
          "ÖDEME TARİHİ": r.paid_at?.seconds
            ? new Date(r.paid_at.seconds * 1000).toLocaleDateString("tr-TR", { timeZone: "Europe/Istanbul" })
            : "",
        };
        productIds.forEach((pid) => {
          const title = selectedSeason.products[pid]?.title ?? pid;
          base[title] = r.receipt_products?.[pid] === true ? "X" : "";
        });
        return base;
      });
      const { utils, writeFileXLSX } = await import("xlsx");
      const ws = utils.json_to_sheet(rows);
      ws["!cols"] = Array(30).fill({ wch: 22 });
      const wb = utils.book_new();
      utils.book_append_sheet(wb, ws, selectedSeasonId);
      writeFileXLSX(wb, `${selectedSeasonId}_odemeler.xlsx`);
      showSnack("Excel indirildi.", "success");
    } catch (e) {
      showSnack("İndirme hatası: " + e.message, "error");
    } finally {
      setDownloading(false);
    }
  };

  if (fetching) return <Box sx={{ textAlign: "center", py: 6 }}><CircularProgress sx={{ color: C.pink }} /></Box>;

  // İstatistik hesapla
  const productIds = Object.keys(selectedSeason?.products ?? {}).sort();
  const soldCounts = {};
  const buyersByProduct = {};
  receipts.forEach((r) => {
    Object.keys(r.receipt_products ?? {}).forEach((pid) => {
      if (r.receipt_products[pid] === true) {
        soldCounts[pid] = (soldCounts[pid] || 0) + 1;
        if (!buyersByProduct[pid]) buyersByProduct[pid] = [];
        buyersByProduct[pid].push({ email: r.email, name: r.name ?? "", paid_at: r.paid_at ?? r.created_at });
      }
    });
  });

  const totalCap  = productIds.reduce((a, pid) => a + (selectedSeason?.products[pid]?.total_stock ?? 0), 0);
  const totalSold = productIds.reduce((a, pid) => a + (soldCounts[pid] ?? 0), 0);
  const totalRev  = receipts.reduce((a, r) => a + (r.price ?? 0), 0);

  const products = productIds.map((pid) => {
    const p     = selectedSeason?.products[pid];
    const sold  = soldCounts[pid] ?? 0;
    const total = p?.total_stock ?? 0;
    return { id: pid, title: p?.title ?? pid, moderator: p?.moderator ?? "—", room: p?.room ?? "—", total, sold, buyers: buyersByProduct[pid] ?? [] };
  });

  return (
    <Box>
      {/* Başlık */}
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3, flexWrap: "wrap", gap: 2 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Typography variant="h6" fontWeight={800}>Geçmiş Sezonlar</Typography>
          {selectedSeason?.is_active && (
            <Chip label="Aktif Sezon" size="small" sx={{ bgcolor: C.greenBg, color: C.green, fontWeight: 700 }} />
          )}
        </Box>
        <Box sx={{ display: "flex", gap: 1.5, alignItems: "center", flexWrap: "wrap" }}>
          <FormControl size="small" sx={{ minWidth: 190 }}>
            <InputLabel>Sezon</InputLabel>
            <Select
              value={selectedSeasonId}
              label="Sezon"
              onChange={(e) => setSelectedSeasonId(e.target.value)}
            >
              {allSeasons.map((s) => (
                <MenuItem key={s.id} value={s.id}>
                  {s.id}
                  {s.is_active && (
                    <Chip label="Aktif" size="small" sx={{ ml: 1, bgcolor: C.pinkLight, color: C.pinkDark, fontSize: 11, fontWeight: 700 }} />
                  )}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button
            variant="outlined"
            startIcon={downloading ? <CircularProgress size={14} /> : <DownloadIcon />}
            onClick={downloadExcel}
            disabled={downloading || !selectedSeason || loading}
            sx={btnOutline}
          >
            Excel İndir
          </Button>
        </Box>
      </Box>

      {loading ? (
        <Box sx={{ textAlign: "center", py: 6 }}><CircularProgress sx={{ color: C.pink }} /></Box>
      ) : !selectedSeason ? (
        <EmptyState text="Sezon bulunamadı." />
      ) : (
        <>
          {/* Özet kartlar */}
          <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 2, mb: 3 }}>
            {[
              { label: "Toplam Kapasite", value: totalCap,                                   color: C.blue,    bg: C.blueBg    },
              { label: "Satılan Bilet",   value: totalSold,                                  color: C.green,   bg: C.greenBg   },
              { label: "Toplam Gelir",    value: `₺${totalRev.toLocaleString("tr-TR")}`,     color: C.pinkDark,bg: C.pinkLight },
              { label: "Toplam İşlem",    value: receipts.length,                             color: C.text,    bg: "#f3f4f6"   },
              { label: "Dolu Atölye",     value: products.filter((p) => p.sold >= p.total && p.total > 0).length, color: C.red, bg: C.redBg },
              { label: "Atölye Sayısı",   value: products.length,                             color: C.orange,  bg: C.orangeBg  },
            ].map((card) => (
              <Paper key={card.label} elevation={0} sx={{ p: 2, border: `1px solid ${C.border}`, borderRadius: 2.5, bgcolor: card.bg }}>
                <Typography sx={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: 0.6 }}>
                  {card.label}
                </Typography>
                <Typography sx={{ fontSize: 24, fontWeight: 800, color: card.color, mt: 0.5 }}>{card.value}</Typography>
              </Paper>
            ))}
          </Box>

          {products.length > 0 ? (
            <StockPanel products={products} />
          ) : (
            <EmptyState text="Bu sezona ait ürün bulunamadı." />
          )}
        </>
      )}
    </Box>
  );
}

// ════════════════════════════════════════════════════════════════════
// YARDIMCI BİLEŞENLER & STİLLER
// ════════════════════════════════════════════════════════════════════
// ════════════════════════════════════════════════════════════════════
// YAKA KARTI TAB
// ════════════════════════════════════════════════════════════════════
function BadgeCardsTab({ activeSeason, showSnack }) {
  const [catIdx, setCatIdx] = useState(0);
  const [texts, setTexts]   = useState({}); // { [catIdx]: "her satırda bir isim" }
  const [busy, setBusy]     = useState(false);

  const cat   = YAKA_KATEGORILER[catIdx];
  const text  = texts[catIdx] || "";
  const names = text.split("\n").map((l) => l.replace(/\s+/g, " ").trim()).filter(Boolean);
  const setText = (t) => setTexts((x) => ({ ...x, [catIdx]: t }));
  // Firebase / Excel isimleri kutudaki mevcut isimlerin altına eklenir
  const appendNames = (arr) => setText(names.concat(arr).join("\n"));
  const dupCount = names.length - new Set(names.map((n) => n.toLocaleLowerCase("tr-TR"))).size;

  const run = async (fn) => {
    setBusy(true);
    try { await fn(); }
    catch (e) { showSnack(e?.message || "İşlem başarısız.", "error"); }
    finally { setBusy(false); }
  };

  const loadFirebase = () => run(async () => {
    const { rows } = await listBadges(activeSeason.id);
    const arr = rows.map((r) => (r.name || "").trim()).filter(Boolean);
    appendNames(arr);
    showSnack(`${arr.length} katılımcı Firebase'den eklendi.`, "success");
  });

  const onFile = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    run(async () => {
      const arr = await readNamesFromExcel(file);
      appendNames(arr);
      showSnack(`${arr.length} isim Excel'den eklendi.`, "success");
    });
  };

  const download = () => run(async () => {
    await downloadYakaKartiPdf(names, catIdx);
    showSnack("PDF hazırlandı.", "success");
  });

  if (!activeSeason) return <EmptyState text="Aktif sezon bulunamadı." />;

  return (
    <Box sx={{ maxWidth: 760 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 3 }}>
        <Typography variant="h6" fontWeight={800}>Yaka Kartları</Typography>
        <Chip label={activeSeason.id} size="small" sx={{ bgcolor: C.pinkLight, color: C.pinkDark, fontWeight: 700 }} />
      </Box>

      <Paper elevation={0} sx={{ p: 2.5, border: `1px solid ${C.border}`, borderRadius: 3 }}>
        <FormControl size="small" sx={{ minWidth: 220, mb: 2 }}>
          <InputLabel>Kategori</InputLabel>
          <Select label="Kategori" value={catIdx} onChange={(e) => setCatIdx(e.target.value)}>
            {YAKA_KATEGORILER.map((c, i) => (
              <MenuItem key={c.id} value={i}>{c.label}</MenuItem>
            ))}
          </Select>
        </FormControl>

        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 1.5 }}>
          {catIdx === 0 && (
            <Button variant="outlined" onClick={loadFirebase} disabled={busy}>
              Firebase'den ekle (ödemesi alınan katılımcılar)
            </Button>
          )}
          <Button variant="outlined" component="label" disabled={busy}>
            Excel'den ekle
            <input hidden type="file" accept=".xlsx,.xls,.csv" onChange={onFile} />
          </Button>
          {names.length > 0 && (
            <Button color="inherit" onClick={() => setText("")} disabled={busy}>Listeyi temizle</Button>
          )}
        </Box>

        <TextField
          multiline
          minRows={8}
          maxRows={18}
          fullWidth
          size="small"
          label={`${cat.label} isimleri (her satıra bir isim)`}
          placeholder={"Ayşe Yılmaz\nMehmet Ali Demir"}
          value={text}
          onChange={(e) => setText(e.target.value)}
        />
        <Typography sx={{ fontSize: 12, color: C.muted, mt: 1 }}>
          Elle yazabilir, yapıştırabilir ya da Firebase / Excel'den ekleyip burada düzeltebilirsiniz.
          {cat.titles && " Ünvanlı yazmak için \"Prof. Dr. | Erdal Arıkan\" biçimini kullanın (Excel'de \"Ünvan\" kolonu da okunur)."}
          Excel'de "Ad Soyad" (ya da "İsim") kolonu yeterli; "Ad" ve "Soyad" ayrı kolonlarsa birleştirilir,
          başlık yoksa ilk kolon alınır.
        </Typography>

        {names.length > 0 && (
          <Box sx={{ mt: 2 }}>
            <Typography sx={{ fontWeight: 700 }}>
              {cat.label}: {names.length} kişi
              {dupCount > 0 && (
                <Typography component="span" sx={{ color: C.orange, fontWeight: 600, ml: 1, fontSize: 13 }}>
                  ({dupCount} aynı isim tekrar ediyor)
                </Typography>
              )}
            </Typography>
            <Button
              variant="contained"
              startIcon={<PictureAsPdfIcon />}
              onClick={download}
              disabled={busy}
              sx={{ mt: 1.5, bgcolor: C.pink, "&:hover": { bgcolor: C.pinkDark } }}
            >
              {busy ? "Hazırlanıyor…" : `PDF indir (${names.length * 2} sayfa)`}
            </Button>
          </Box>
        )}
      </Paper>
    </Box>
  );
}

function EmptyState({ icon, text }) {
  return (
    <Box sx={{ py: 8, textAlign: "center", color: C.muted }}>
      {icon && <Box sx={{ mb: 1 }}>{icon}</Box>}
      <Typography sx={{ fontSize: 14 }}>{text}</Typography>
    </Box>
  );
}

const tablePaper = {
  border: `1px solid ${C.border}`,
  borderRadius: 2.5,
  overflow: "hidden",
};

const th = {
  fontWeight: 700,
  fontSize: 12,
  color: C.muted,
  textTransform: "uppercase",
  letterSpacing: 0.5,
  py: 1.2,
};

// Günlük tablo: başlık ve gövde ayrı tablolarda olduğu için sütun genişlikleri ortak
function DailyCols() {
  return (
    <colgroup>
      <col style={{ width: 40 }} />
      {Array.from({ length: 5 }, (_, i) => <col key={i} />)}
    </colgroup>
  );
}

const btnPink = {
  bgcolor: C.pink,
  "&:hover": { bgcolor: C.pinkDark },
  fontWeight: 700,
  boxShadow: "none",
  textTransform: "none",
  borderRadius: 2,
};

const btnOutline = {
  borderColor: C.pink,
  color: C.pink,
  fontWeight: 700,
  textTransform: "none",
  borderRadius: 2,
  "&:hover": { borderColor: C.pinkDark, bgcolor: C.pinkLight },
};
